// API Request and Response Types using Zod

import { z } from 'zod';
import { parseYouTubeId } from '../youtube.js';

// ========== Common Response Types ==========
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  });

export type ApiResponse<T = any> = {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
};

// ========== Auth Endpoints ==========
export namespace AuthAPI {
  export const WebhookRequestSchema = z.object({
    type: z.enum(['user.created', 'user.updated', 'user.deleted']),
    data: z.any(),
  });

  export type WebhookRequest = z.infer<typeof WebhookRequestSchema>;
  export type WebhookResponse = ApiResponse<void>;

  // ---- One-time email sign-in codes ----------------------------------------
  // Both endpoints are public (no bearer token) — they are how a session gets
  // created in the first place.

  // POST /api/auth/email-code/request
  // Answers 404 when a sign-in names an address with no account, so the UI can
  // say so rather than mailing a code nobody will receive.
  export const RequestEmailCodeRequestSchema = z.object({
    email: z.string().email(),
    /** Sign-up surfaces expect unknown addresses; the account is created on
     *  verify. Omitted/false means a sign-in, where unknown is an error. */
    isSignUp: z.boolean().optional(),
  });
  export const RequestEmailCodeDataSchema = z.object({
    /** Lifetime of the code just issued. */
    expiresInSeconds: z.number(),
    /** How long the client should disable "resend" for. */
    resendAfterSeconds: z.number(),
  });
  export type RequestEmailCodeRequest = z.infer<typeof RequestEmailCodeRequestSchema>;
  export type RequestEmailCodeData = z.infer<typeof RequestEmailCodeDataSchema>;
  export type RequestEmailCodeResponse = ApiResponse<RequestEmailCodeData>;

  // POST /api/auth/email-code/verify
  // Returns a Firebase custom token the client exchanges via
  // signInWithCustomToken — the same primitive the native bridge uses.
  export const VerifyEmailCodeRequestSchema = z.object({
    email: z.string().email(),
    code: z.string().regex(/^\d{6}$/),
    /** Only used when this address has no account yet; together they become
     *  the Firebase displayName, which GET /api/user/profile splits into
     *  first/last name. */
    firstName: z.string().trim().min(1).max(60).optional(),
    lastName: z.string().trim().min(1).max(60).optional(),
  });
  export const VerifyEmailCodeDataSchema = z.object({
    customToken: z.string(),
    /** True when this verification created the Firebase account. */
    isNewUser: z.boolean(),
  });
  export type VerifyEmailCodeRequest = z.infer<typeof VerifyEmailCodeRequestSchema>;
  export type VerifyEmailCodeData = z.infer<typeof VerifyEmailCodeDataSchema>;
  /** Machine-readable cause of a failed verify, alongside the user-facing `error`. */
  export type VerifyEmailCodeFailureReason =
    | "invalid_code"
    | "expired"
    | "too_many_attempts"
    | "not_configured";
  export type VerifyEmailCodeResponse = ApiResponse<VerifyEmailCodeData> & {
    reason?: VerifyEmailCodeFailureReason;
  };
}

// ========== User Endpoints ==========
export namespace UserAPI {
  // Quiz answers schema (all fields required when submitting quiz)
  export const QuizAnswersSchema = z.object({
    location: z.string(),
    dinnerVibe: z.string(),
    groupRole: z.string(),
    communicationStyle: z.string(),
    groupChatRole: z.string(),
    humorStyle: z.string(),
    careerStage: z.string(),
    industry: z.string(),
    unwindActivities: z.array(z.string()),
    weekendEnergy: z.array(z.string()),
    conversationTopics: z.array(z.string()),
    hobbies: z.array(z.string()),
    socialBattery: z.string(),
    relationshipStatus: z.string(),
    genderIdentity: z.array(z.string()),
    pronouns: z.array(z.string()),
    sexualOrientation: z.array(z.string()),
    birthday: z.string(),
    primaryValue1: z.string(),
    primaryValue2: z.string(),
    conflictStyle: z.string(),
    feedbackStyle: z.string(),
    communityPreference: z.string(),
    politicalViews: z.number().min(1).max(7),
    topicsOpenTo: z.array(z.string()),
    /**
     * Community picker (Mixed / Men / Sapphic) from the OnboardingQuiz
     * stepper's first step. Distinct from the values-section
     * `communityPreference` (similar vs diverse perspectives).
     */
    community: z.enum(["men", "sapphic", "mixed"]).optional(),
    /**
     * Multi-select age preferences relative to the user's own age picked on
     * the age-intro step ("younger" / "same" / "older").
     */
    ageRangePreferences: z
      .array(z.enum(["younger", "same", "older"]))
      .optional(),
    /**
     * Narrative-intent questions asked only by the v3 onboarding flow
     * (components/onboarding). `connectionIntent` is the "which of these
     * sounds like you" mirror; `meetingChannels` is how they meet people
     * today. Both feed the personalized reveal screen and give the matcher
     * an early read on motivation.
     */
    connectionIntent: z.array(z.string()).optional(),
    meetingChannels: z.array(z.string()).optional(),
    // Richer matching / kiki signal, collected in the Connection Profile.
    dietaryNeeds: z.array(z.string()).optional(),
    drinking: z.string().optional(),
    cityTenure: z.string().optional(),
    buddyActivities: z.array(z.string()).optional(),
  });

  export type QuizAnswers = z.infer<typeof QuizAnswersSchema>;

  // POST /api/user/profile - Partial update schema
  export const UpdateProfileRequestSchema = z.object({
    // Quiz answers - all optional for partial updates
    quizAnswers: QuizAnswersSchema.partial().optional(),
    // Direct profile fields - all optional for partial updates
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    instagram: z.string().optional(),
    // Empty string clears the picture and reverts to the auth-provider photo.
    profileImageUrl: z.string().url().or(z.literal("")).optional(),
    // Event-type slugs the user is interested in (from shared/eventTypes.ts).
    interests: z.array(z.string()).optional(),
    // Public expressive layer: a short bio + freeform "into" culture tags.
    bio: z.string().max(300).optional(),
    interestTags: z.array(z.string()).optional(),
    // Freeform profile prompts (stored together in the `profileExtras` column,
    // separate from the matching quiz). Hyphens survive here — unlike
    // quizAnswers, which the converter normalizes "-" → "_".
    currentlyConsuming: z.array(z.string()).optional(),
    lookingTo: z.array(z.string()).optional(),
    education: z.string().max(120).optional(),
    work: z.string().max(120).optional(),
    // First-party signup attribution, read from the landing URL (sessionStorage)
    // and sent once with the onboarding save. Server writes it only if the user
    // has no attribution yet. utm_content carries the Meta ad id.
    signupAttribution: z.object({
      utmSource: z.string().max(255).nullable().optional(),
      utmMedium: z.string().max(255).nullable().optional(),
      utmCampaign: z.string().max(255).nullable().optional(),
      utmContent: z.string().max(255).nullable().optional(),
      utmTerm: z.string().max(255).nullable().optional(),
      fbclid: z.string().max(512).nullable().optional(),
      gclid: z.string().max(512).nullable().optional(),
      landingPage: z.string().max(512).nullable().optional(),
    }).optional(),
  });

  export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;
  export type GetProfileResponse = ApiResponse<{
    user: any;
    profile: any;
    isAdmin: boolean;
    hasAnalyticsAccess: boolean;
    entitlements: Entitlement[];
    // True when the user has the native app installed (an ios/android device
    // is registered for push) — gates the web "install the app" nudges.
    hasAppDevice: boolean;
    // True when the user holds an active booking. Gates the native app's
    // notification-permission ask: anyone with a seat is owed the pre-dinner
    // reminders, so the ask follows them rather than living on one screen.
    hasBooking: boolean;
  }>;
  // `quizCompleted` is the server's own verdict after the write, so the client
  // can tell a save that completed the profile from one that only looked like
  // it should have (see lib/onboardingSave).
  export type UpdateProfileResponse = ApiResponse<{ quizCompleted: boolean }>;

  // GET /api/user/settings · POST /api/user/settings
  // Communication / notification preferences. Add new toggles here.
  export const UserSettingsSchema = z.object({
    weeklyEventSuggestionsEmail: z.boolean(),
    recommendedEventsNotification: z.boolean(),
  });
  export type UserSettings = z.infer<typeof UserSettingsSchema>;
  export type GetSettingsResponse = ApiResponse<UserSettings>;

  export const UpdateSettingsRequestSchema = UserSettingsSchema.partial();
  export type UpdateSettingsRequest = z.infer<typeof UpdateSettingsRequestSchema>;
  export type UpdateSettingsResponse = ApiResponse<UserSettings>;

  // GET /api/user/:userId/public — a user's public appearance (safe subset,
  // no phone/email/instagram/exact birthday or quiz answers).
  export interface PublicProfile {
    id: string;
    firstName: string;
    lastInitial: string | null;
    profileImageUrl: string | null;
    cityName: string | null;
    age: number | null;
    pronouns: string[];
    bio: string | null;
    interestTags: string[];
    currentlyConsuming: string[];
    lookingTo: string[];
  }
  export type GetPublicProfileResponse = ApiResponse<PublicProfile>;

  // GET /api/user/neighborhoods
  export type GetNeighborhoodsResponse = ApiResponse<Array<{
    id: string;
    name: string;
    cityId: string;
    cityName: string;
    isActive: boolean;
    freeUntil: string | null;
  }>>;

  // GET /api/user/cities/search?query=xyz
  export const CitySearchResultSchema = z.object({
    id: z.string(),
    name: z.string(),
    emoji: z.string(),
    available: z.boolean(),
    localLanguage: z.string().optional(),
  });

  export type CitySearchResult = z.infer<typeof CitySearchResultSchema>;
  export type GetCitiesResponse = ApiResponse<CitySearchResult[]>;

  // GET /api/user/connections
  export const UserConnectionSchema = z.object({
    id: z.string(),
    connectedUser: z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      city: z.string().nullable().optional(),
      profileImageUrl: z.string().nullable().optional(),
    }),
    contactMethod: z.string().nullable(),
    contactInfo: z.object({
      email: z.string().optional(),
      phone: z.string().optional(),
      instagram: z.string().optional(),
    }).optional(),
    mySharedMethods: z.array(z.string()).optional(),
    isMutual: z.boolean(),
    createdAt: z.string(),
  });

  export type UserConnection = z.infer<typeof UserConnectionSchema>;
  export type GetConnectionsResponse = ApiResponse<UserConnection[]>;

  // POST /api/user/post-dinner/connections - Update connections for a booking
  export const UpdateConnectionsRequestSchema = z.object({
    bookingId: z.string().optional(),
    connections: z.array(z.object({
      toUserId: z.string().min(1, "User ID is required"),
      // Empty array means remove all connections for this user
      contactMethods: z.array(z.enum(["email", "phone", "instagram"]))
    })).min(1, "At least one connection is required")
  });

  export type UpdateConnectionsRequest = z.infer<typeof UpdateConnectionsRequestSchema>;
  export type UpdateConnectionsResponse = ApiResponse<void>;

  // POST /api/user/feedback
  export const SubmitFeedbackRequestSchema = z.object({
    feedback: z.string().min(1, "Feedback is required").max(2000, "Feedback is too long"),
  });

  export type SubmitFeedbackRequest = z.infer<typeof SubmitFeedbackRequestSchema>;
  export type SubmitFeedbackResponse = ApiResponse<void>;

  // DELETE /api/user/account - Delete user account
  export const DeleteAccountRequestSchema = z.object({
  });
  export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequestSchema>;
  export type DeleteAccountResponse = ApiResponse<{
    deletedServices: string[];
  }>;

  // GET /api/chat/auth/token
  export const GetAuthTokenDataSchema = z.object({
    token: z.string(),
  });
  export type GetAuthTokenData = z.infer<typeof GetAuthTokenDataSchema>;
  export type GetAuthTokenResponse = ApiResponse<GetAuthTokenData>;

  // GET /api/user/chat/table/:tableId - Table info for chat (restaurant, attendees); only when table is closed (locked)
  export const ChatTableAttendeeSchema = z.object({
    displayName: z.string(), // e.g. "Jane D."
  });
  export const ChatTableInfoDataSchema = z.object({
    restaurantName: z.string(),
    reservationName: z.string().nullable(),
    address: z.string().nullable(),
    googleMapsLink: z.string().nullable(),
    isClosed: z.boolean(),
    eventDate: z.string(), // YYYY-MM-DD
    eventTime: z.string(), // HH:MM or HH:MM:SS
    attendees: z.array(ChatTableAttendeeSchema),
  });
  export type ChatTableAttendee = z.infer<typeof ChatTableAttendeeSchema>;
  export type ChatTableInfoData = z.infer<typeof ChatTableInfoDataSchema>;
  export type GetChatTableInfoResponse = ApiResponse<ChatTableInfoData>;

  // Generic chat info — stored directly on a Firestore conversation doc and
  // editable from the admin panel. Used for chats that aren't backed by a
  // postgres table (e.g. group/neighborhood chats) but still want a venue
  // and meeting time displayed in the chat header.
  export const ChatVenueSchema = z.object({
    name: z.string(),
    address: z.string().nullable(),
    googleMapsLink: z.string().nullable(),
  });
  export const ChatInfoDataSchema = z.object({
    venue: ChatVenueSchema,
    meetingTime: z.string(), // ISO 8601 datetime, e.g. "2026-05-06T19:30:00Z"
    description: z.string().nullable(), // free-form note rendered at the top of the chat header expansion
    eventLink: z.string().nullable().optional(), // in-app link to the source event (e.g. "/events/{slug}")
  });
  export type ChatVenue = z.infer<typeof ChatVenueSchema>;
  export type ChatInfoData = z.infer<typeof ChatInfoDataSchema>;

  // GET /api/user/events
  export type GetEventsResponse = ApiResponse<Array<{
    id: string;
    date: string;
    time: string;
    type: string | null;
    neighborhoodIds: string[] | null;
  }>>;

  // Reservation (Booking) Types
  export const ReservationEventTimeSchema = z.object({
    id: z.string(),
    date: z.string(),
    time: z.string(),
    type: z.string().nullable().optional(),
  });

  export const ReservationNeighborhoodSchema = z.object({
    id: z.string(),
    name: z.string(),
    freeUntil: z.string().nullable().optional(),
  });

  export const ReservationTableSchema = z.object({
    id: z.string().nullable(),
    capacity: z.number().nullable(),
    state: z.enum(['open', 'locked']).nullable(),
    reservationStatus: z.enum(['needs_reservation', 'reserved', 'confirmed', 'cancelled']).nullable(),
    reservationName: z.string().nullable(),

    restaurantId: z.string().nullable(),
    restaurantName: z.string().nullable(),
    address: z.string().nullable(),
    website: z.string().nullable(),
    googleMapsLink: z.string().nullable(),
  });

  export const CalendarLinksSchema = z.object({
    icsUrl: z.string(),
    googleCalendarUrl: z.string(),
  });

  export const ReservationCloseOutBarSchema = z.object({
    name: z.string(),
    address: z.string().nullable(),
    website: z.string().nullable(),
    googleMapsLink: z.string().nullable(),
  });

  export const ReservationCloseOutSchema = z.object({
    /** Firestore conversation id once the chat has been opened, else null. */
    conversationId: z.string().nullable(),
    /** "scheduled" before the chat opens, "open" once it has. */
    state: z.enum(['scheduled', 'open']),
    /** Bar venue. Only populated once the chat is open. */
    bar: ReservationCloseOutBarSchema.nullable(),
  });

  export const ReservationSchema = z.object({
    id: z.string(),
    userId: z.string(),
    eventTimeId: z.string(),
    neighborhoodId: z.string(),
    status: z.enum(['pending', 'active', 'cancelled', 'cant_be_seated']),
    assignmentStatus: z.enum(['pending', 'confirmed']),
    confirmedAt: z.date().nullable().optional(), // optional because it might not be populated in all contexts yet
    localEventDate: z.date(),
    eventTimezone: z.string(),
    bookingTimes: z.object({
      eventTime: z.date(),
      cancelableUntil: z.date(),
      showEntryUntil: z.date(),
    }),
    cancelledAt: z.date().nullable(),
    cancellationReason: z.string().nullable(),
    specialRequests: z.string().nullable(),
    entitlementId: z.string().nullable(),
    entitlementType: z.enum(['one_time', 'subscription', 'free']).nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    eventTime: ReservationEventTimeSchema,
    neighborhood: ReservationNeighborhoodSchema,

    table: ReservationTableSchema.nullable(),
    feedbackCompleted: z.date().nullable(),
    calendarLinks: CalendarLinksSchema.optional(),
    closeOut: ReservationCloseOutSchema.nullable().optional(),
  });

  export type Reservation = z.infer<typeof ReservationSchema>;
  export type ReservationEventTime = z.infer<typeof ReservationEventTimeSchema>;
  export type ReservationNeighborhood = z.infer<typeof ReservationNeighborhoodSchema>;

  // GET /api/user/reservations
  export type GetReservationsResponse = ApiResponse<Reservation[]>;

  // Booking Details Types
  export const BudgetPreferenceSchema = z.enum(['$', '$$', '$$$']);
  export type BudgetPreference = z.infer<typeof BudgetPreferenceSchema>;

  export const AgePreferenceSchema = z.enum(['my_age', 'older', 'younger']);
  export type AgePreference = z.infer<typeof AgePreferenceSchema>;

  export const GenderPreferenceSchema = z.enum(['same', 'mixed', 'male', 'sapphic']);
  export type GenderPreference = z.infer<typeof GenderPreferenceSchema>;

  export const LanguagePreferenceSchema = z.enum(['english', 'local']);
  export type LanguagePreference = z.infer<typeof LanguagePreferenceSchema>;

  export const SpecialRequestsSchema = z.object({
    specialRequestsText: z.string().optional(),
    preferSapphicTable: z.boolean().optional(),
    budget: z.array(BudgetPreferenceSchema).optional(),
    age: z.array(AgePreferenceSchema).optional(),
    gender: z.array(GenderPreferenceSchema).optional(),
    language: z.array(LanguagePreferenceSchema).optional(),
  });

  export type SpecialRequests = z.infer<typeof SpecialRequestsSchema>;

  // POST /api/user/reservations
  export const CreateReservationRequestSchema = z.object({
    eventTimeId: z.string(),
    neighborhoodId: z.string(),
    specialRequests: z.string().optional(),
    replaceBookingId: z.string().optional(),
    // Send true ONLY from a booking flow that showed the no-show fee disclaimer
    // and got the guest's agreement. Stored on the booking; it is the consent a
    // no-show charge rests on, so it must never be defaulted to true.
    noShowDisclaimerAccepted: z.boolean().optional(),
  });

  export type CreateReservationRequest = z.infer<typeof CreateReservationRequestSchema>;


  // Payment & Entitlement Types
  export const PaymentOptionSchema = z.object({
    id: z.string(),
    type: z.enum(['one_time', 'subscription']),
    name: z.string(),
    description: z.string(),
    price: z.number(),
    currency: z.string(),
    priceId: z.string(),
    appliedBalanceAmount: z.number().optional(),
    interval_months: z.number().optional(),
    original_amount: z.number().optional(), // Pre-coupon/referral price if discount applied
    couponId: z.string().optional(), // Stripe coupon ID or referral code if discount applied
    coupon_once: z.boolean().optional(), // Whether the coupon is once-off or recurring
  });
  
  export const CreateReservationResponseSchema = z.object({
    booking: ReservationSchema,
    paymentOptions: z.array(PaymentOptionSchema).optional(),
    requiresPayment: z.boolean().optional(),
    startDate: z.date().nullable().optional(),
  });
  
  export type CreateReservationResponseData = z.infer<typeof CreateReservationResponseSchema>;
  export type CreateReservationResponse = ApiResponse<CreateReservationResponseData>;
  
  // PATCH /api/user/reservations/:id
  export const UpdateReservationRequestSchema = z.object({
    status: z.enum(['active', 'cancelled']),
    cancellationReason: z.enum([
      'schedule_conflict',
      'feeling_unwell',
      'emergency',
      'changed_mind',
      'other',
    ]).optional(),
  });

  export type UpdateReservationRequest = z.infer<typeof UpdateReservationRequestSchema>;
  export type UpdateReservationResponse = ApiResponse<void>;
  
  // GET /api/user/reservations/:id
  export type GetReservationByIdResponse = ApiResponse<Reservation>;

  // POST /api/user/reservations/:id/payment
  export const CreateBookingPaymentRequestSchema = z.object({
    bookingId: z.string().optional(),
    paymentOptionId: z.string(),
    couponId: z.string().optional(), // Stripe coupon ID if discount was applied
    referral: z.string().optional(), // Rewardful referral code
    // Native device attribution identifiers (from the bridge), carried into the
    // Stripe intent metadata so the webhook can fire AppsFlyer S2S purchase
    // events. Omitted on web.
    deviceIdentifiers: z
      .object({
        appsflyerId: z.string().optional(),
        advertisingId: z.string().optional(),
        platform: z.enum(['ios', 'android']).optional(),
      })
      .optional(),
  });

  // POST /api/user/payment/payment-options - Get payment options with optional coupon
  export const GetPaymentOptionsRequestSchema = z.object({
    couponCode: z.string().optional(),
    bookingId: z.string().optional(),
  });

  export const GetPaymentOptionsDataSchema = z.object({
    paymentOptions: z.array(PaymentOptionSchema),
    couponCode: z.string().optional(), // Coupon or referral code that was applied
    startDate: z.date().nullable().optional(),
  });

  export type GetPaymentOptionsRequest = z.infer<typeof GetPaymentOptionsRequestSchema>;
  export type GetPaymentOptionsData = z.infer<typeof GetPaymentOptionsDataSchema>;
  export type GetPaymentOptionsResponse = ApiResponse<GetPaymentOptionsData>;
  
  // Deprecated: Use GetPaymentOptionsRequest instead
  export const ValidateCouponRequestSchema = z.object({
    couponCode: z.string(),
  });

  export const ValidateCouponDataSchema = z.object({
    paymentOptions: z.array(PaymentOptionSchema),
    couponCode: z.string(),
  });

  export type ValidateCouponRequest = z.infer<typeof ValidateCouponRequestSchema>;
  export type ValidateCouponData = z.infer<typeof ValidateCouponDataSchema>;
  export type ValidateCouponResponse = ApiResponse<ValidateCouponData>;

  export const CreateBookingPaymentDataSchema = z.object({
    clientSecret: z.string(),
    type: z.enum(['payment', 'subscription', 'free']),
    subscriptionId: z.string().optional(),
    setupIntentId: z.string().optional(),
  });

  export type CreateBookingPaymentRequest = z.infer<typeof CreateBookingPaymentRequestSchema>;
  export type CreateBookingPaymentData = z.infer<typeof CreateBookingPaymentDataSchema>;
  export type CreateBookingPaymentResponse = ApiResponse<CreateBookingPaymentData>;


  export const EntitlementSchema = z.object({
    id: z.string(),
    userId: z.string(),
    paymentId: z.string(),
    type: z.enum(['one_time', 'subscription', 'free']),
    status: z.enum(['active', 'consumed', 'cancelled']),
    stripeSubscriptionId: z.string().nullable().optional(),
    begin: z.date().nullable().optional(),
    end: z.date().nullable().optional(),
    renewalInterval: z.string().nullable().optional(), // e.g., "monthly", "yearly", etc.
    renewsAt: z.date().nullable().optional(),
    managementUrl: z.string().nullable().optional(), // URL to manage subscription (e.g., App Store, Play Store)
    createdAt: z.date(),
    updatedAt: z.date(),
  });

  export const PaymentSchema = z.object({
    id: z.string(),
    stripeCustomerId: z.string().nullable(),
    stripePaymentSessionsId: z.string(),
    paymentAmount: z.string(),
    currency: z.string(),
    status: z.enum(['pending', 'succeeded', 'failed', 'refunded']),
    type: z.enum(['one_time', 'subscription']),
    productId: z.string().nullable(),
    refundId: z.string().nullable(),
    refundAmount: z.string().nullable(),
    refundedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
  });

  export type PaymentOption = z.infer<typeof PaymentOptionSchema>;
  export type Entitlement = z.infer<typeof EntitlementSchema>;
  export type Payment = z.infer<typeof PaymentSchema>;

  // GET /api/user/payment/payment-history
  export const PaymentHistoryDataSchema = z.array(PaymentSchema);

  export type PaymentHistoryData = z.infer<typeof PaymentHistoryDataSchema>;
  export type GetPaymentHistoryResponse = ApiResponse<PaymentHistoryData>;

  // POST /api/user/payment/cancel-subscription
  export const CancelSubscriptionRequestSchema = z.object({
    entitlementId: z.string(),
  });

  export const CancelSubscriptionDataSchema = z.object({
    entitlement: EntitlementSchema,
    cancelAtPeriodEnd: z.boolean(),
    periodEnd: z.date().nullable(),
  });

  export type CancelSubscriptionRequest = z.infer<typeof CancelSubscriptionRequestSchema>;
  export type CancelSubscriptionData = z.infer<typeof CancelSubscriptionDataSchema>;
  export type CancelSubscriptionResponse = ApiResponse<CancelSubscriptionData>;

  // ========== Booking Invites (+1) ==========
  export const BookingInviteStatusSchema = z.enum([
    "pending",
    "accepted",
    "declined",
    "cancelled",
    "expired",
  ]);
  export type BookingInviteStatus = z.infer<typeof BookingInviteStatusSchema>;

  // POST /api/user/reservations/:bookingId/invites
  export const CreateBookingInviteRequestSchema = z.object({
    email: z.string().email().optional(),
    inviteeUserId: z.string().optional(),
  }).refine(
    (data) => !!(data.email || data.inviteeUserId),
    { message: "Either email or inviteeUserId is required" },
  );
  export type CreateBookingInviteRequest = z.infer<typeof CreateBookingInviteRequestSchema>;

  export const BookingInviteSchema = z.object({
    id: z.string(),
    code: z.string(),
    status: BookingInviteStatusSchema,
    /**
     * Relative to the booking that fetched this invite: "sent" when this
     * booking is the inviter, "received" when it's the invitee. Drives which
     * name + affordances the roster UI shows.
     */
    direction: z.enum(["sent", "received"]).optional(),
    email: z.string().nullable(),
    inviteeUserId: z.string().nullable(),
    inviteeBookingId: z.string().nullable(),
    invitee: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string().nullable(),
      })
      .nullable(),
    inviter: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string().nullable(),
      }),
    inviterBookingId: z.string(),
    eventTimeId: z.string(),
    neighborhoodId: z.string(),
    acceptedAt: z.string().nullable(),
    createdAt: z.string(),
    shareUrl: z.string().optional(),
  });
  export type BookingInvite = z.infer<typeof BookingInviteSchema>;
  export type CreateBookingInviteResponse = ApiResponse<BookingInvite>;

  // GET /api/user/reservations/:bookingId/invites
  export type ListBookingInvitesResponse = ApiResponse<BookingInvite[]>;

  // GET /api/user/invites/incoming — pending invites targeted at the current
  // user (by inviteeUserId or email). Same preview shape as
  // BookingInvitePreviewSchema plus the invite id (so the dashboard can deep
  // link to /invite/:code or call accept directly).
  export const IncomingBookingInviteSchema = z.object({
    id: z.string(),
    code: z.string(),
    status: BookingInviteStatusSchema,
    inviter: z.object({
      firstName: z.string(),
      lastName: z.string().nullable(),
    }),
    event: z.object({
      id: z.string(),
      date: z.string(),
      time: z.string(),
      type: z.string().nullable(),
    }),
    neighborhood: z.object({
      id: z.string(),
      name: z.string(),
      cityId: z.string(),
    }),
  });
  export type IncomingBookingInvite = z.infer<typeof IncomingBookingInviteSchema>;
  export type ListIncomingInvitesResponse = ApiResponse<IncomingBookingInvite[]>;

  // POST /api/user/invites/:id/cancel
  export type CancelBookingInviteResponse = ApiResponse<void>;

  // GET /api/invites/:code  (public — preview before sign-in)
  export const BookingInvitePreviewSchema = z.object({
    code: z.string(),
    status: BookingInviteStatusSchema,
    inviter: z.object({
      firstName: z.string(),
      lastName: z.string().nullable(),
    }),
    event: z.object({
      id: z.string(),
      date: z.string(),
      time: z.string(),
      type: z.string().nullable(),
    }),
    neighborhood: z.object({
      id: z.string(),
      name: z.string(),
      cityId: z.string(),
      cityName: z.string().nullable(),
    }),
  });
  export type BookingInvitePreview = z.infer<typeof BookingInvitePreviewSchema>;
  export type GetBookingInvitePreviewResponse = ApiResponse<BookingInvitePreview>;

  // POST /api/user/invites/:code/accept
  // Three shapes the server returns:
  //   - active booking exists → { booking, requiresBooking: false, requiresPayment: false }
  //   - no booking yet → { booking: null, requiresBooking: true, eventTimeId, neighborhoodId }
  //   - (legacy) pending booking that needs payment → requiresPayment: true
  export const AcceptBookingInviteDataSchema = z.object({
    booking: z.any().nullable(),
    requiresPayment: z.boolean(),
    requiresBooking: z.boolean().optional(),
    inviteId: z.string(),
    eventTimeId: z.string().optional(),
    neighborhoodId: z.string().optional(),
    cityId: z.string().optional(),
  });
  export type AcceptBookingInviteData = z.infer<typeof AcceptBookingInviteDataSchema>;
  export type AcceptBookingInviteResponse = ApiResponse<AcceptBookingInviteData>;
}

// ========== Admin Endpoints ==========
export namespace AdminAPI {
  // Database Model Types
  export const EventTimeSchema = z.object({
    id: z.string(),
    date: z.string(),
    time: z.string(),
    type: z.string().nullable().optional(),
    neighborhoodIds: z.array(z.string()).nullable().optional(),
  });

  export const BookingUserSchema = z.object({
    id: z.string(),
    username: z.string(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
  });

  export const BookingNeighborhoodSchema = z.object({
    id: z.string(),
    name: z.string(),
  });

  export const BookingSchema = z.object({
    id: z.string(),
    userId: z.string(),
    eventTimeId: z.string(),
    neighborhoodId: z.string(),
    bookingStatus: z.enum(['pending', 'confirmed', 'cancelled']),
    status: z.enum(['pending', 'active', 'cancelled']).optional(),
    confirmedAt: z.date().nullable().optional(),
    specialRequests: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    user: BookingUserSchema.nullable(),
    neighborhood: BookingNeighborhoodSchema.nullable(),
  });

  export const EventOverviewSchema = z.object({
    id: z.string(),
    date: z.string(),
    time: z.string(),
    type: z.string().nullable().optional(),
    neighborhoodIds: z.array(z.string()).nullable().optional(),
    bookingsCount: z.number(),
    activeBookingsCount: z.number(),
    pendingBookingsCount: z.number(),
    cantBeSeatedBookingsCount: z.number(),
  });

  export type EventTime = z.infer<typeof EventTimeSchema>;
  export type Booking = z.infer<typeof BookingSchema>;
  export type BookingUser = z.infer<typeof BookingUserSchema>;
  export type BookingNeighborhood = z.infer<typeof BookingNeighborhoodSchema>;
  export type EventOverview = z.infer<typeof EventOverviewSchema>;

  // Restaurant and Table schemas
  export const RestaurantSchema = z.object({
    id: z.string(),
    name: z.string(),
    address: z.string(),
    neighborhoodId: z.string(),
    neighborhoodName: z.string().nullable(),
    type: z.string().default("restaurant"), // restaurant, bar, cafe, etc.
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    googleMapsLink: z.string().nullable(),
    bookingLink: z.string().nullable(),
    tags: z.array(z.string()).nullable(),
    isActive: z.boolean(),
  });

  export const AvailableTableSchema = z.object({
    id: z.string(),
    eventTimeId: z.string(),
    restaurantId: z.string(),
    capacity: z.number(),
    state: z.enum(['open', 'locked']),
    reservationName: z.string().nullable(),
    reservationStatus: z.enum(['needs_reservation', 'reserved', 'confirmed', 'cancelled']),
    reservationDone: z.date().nullable().optional(),
    restaurant: RestaurantSchema,
  });

  export const TableAssignmentSchema = z.object({
    id: z.string(),
    bookingId: z.string(),
    availableTableId: z.string(),
    confirmedAt: z.date().nullable(),
    assignedAt: z.date(),
  });

  export type Restaurant = z.infer<typeof RestaurantSchema>;
  export type AvailableTable = z.infer<typeof AvailableTableSchema>;
  export type TableAssignment = z.infer<typeof TableAssignmentSchema>;

  // POST /api/admin/events
  export const CreateEventRequestSchema = z.object({
    date: z.string(),
    time: z.string(),
    type: z.enum(["dinner", "brunch", "drinks", "coffee"]).optional(),
    neighborhoodIds: z.array(z.string()).nullable().optional(),
  });

  export type CreateEventRequest = z.infer<typeof CreateEventRequestSchema>;
  export type CreateEventResponse = ApiResponse<EventTime>;

  // PATCH /api/admin/events/:id
  export const UpdateEventRequestSchema = z.object({
    date: z.string().optional(),
    time: z.string().optional(),
    type: z.enum(["dinner", "brunch", "drinks", "coffee"]).optional(),
    neighborhoodIds: z.array(z.string()).nullable().optional(),
  });

  export type UpdateEventRequest = z.infer<typeof UpdateEventRequestSchema>;
  export type UpdateEventResponse = ApiResponse<void>;

  // DELETE /api/admin/events/:id
  export type DeleteEventResponse = ApiResponse<void>;

  // GET /api/admin/events
  export type GetEventsResponse = ApiResponse<EventOverview[]>;

  // GET /api/admin/events/:id
  export type GetEventDetailsResponse = ApiResponse<{
    event: EventTime;
    bookings: Booking[];
    tables: AvailableTable[];
    assignments: TableAssignment[];
    /**
     * Only accepted "+1" invites — pending/declined/cancelled are intentionally
     * filtered server-side so the matching UI has a single, clean signal.
     */
    acceptedInvites?: Array<{
      id: string;
      inviterUserId: string;
      inviteeUserId: string | null;
      inviterBookingId: string;
      inviteeBookingId: string | null;
    }>;
    /**
     * Internal staff notes keyed by userId, for every user booked on this
     * event. Admin-only — shown in the matching UI, never to the user.
     */
    userNotes?: Record<string, UserNote[]>;
  }>;

  // POST /api/admin/events/:id/match
  export const MatchingAssignmentSchema = z.object({
    userId: z.string(),
    userName: z.string(),
    score: z.number(),
    reasons: z.array(z.string()),
  });

  export const MatchingResultSchema = z.object({
    matchData: z.array(z.string()),
  });

  export type MatchingAssignment = z.infer<typeof MatchingAssignmentSchema>;
  export type MatchingResult = z.infer<typeof MatchingResultSchema>;
  export type GenerateMatchesResponse = ApiResponse<MatchingResult>;

  // POST /api/admin/bookings/:bookingId/reassign — the unified entry point for
  // every table-membership change (assign, move, unassign, cant_be_seated).
  // Handles chat sync, broadcasts, and entitlement re-activation internally.
  export const ReassignBookingRequestSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("assign"), tableId: z.string() }),
    z.object({ action: z.literal("unassign") }),
    z.object({ action: z.literal("cant_be_seated") }),
  ]);
  export type ReassignBookingRequest = z.infer<typeof ReassignBookingRequestSchema>;
  export type ReassignBookingResponse = ApiResponse<{
    /** True when an apology email was sent (cant_be_seated path). */
    emailSent?: boolean;
    /** Whether the call resulted in any state change (false means no-op). */
    changed: boolean;
  }>;

  // POST /api/admin/tables/:tableId/reassign-all — batch variant of the
  // booking reassign: applies the same action to every active booking on a
  // table (move all to another table, unassign all, or mark all
  // cant_be_seated). The cant_be_seated path refunds every guest and sends a
  // single apology broadcast for the whole table.
  export const BatchReassignTableRequestSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("assign"), tableId: z.string() }),
    z.object({ action: z.literal("unassign") }),
    z.object({ action: z.literal("cant_be_seated") }),
  ]);
  export type BatchReassignTableRequest = z.infer<typeof BatchReassignTableRequestSchema>;
  export type BatchReassignTableResponse = ApiResponse<{
    /** Bookings that were on the source table. */
    total: number;
    /** How many actually changed state. */
    changed: number;
    /** User ids of the changed bookings — for follow-up emails. */
    changedUserIds: string[];
    /** Per-booking failures; empty when everything succeeded. */
    failed: Array<{ bookingId: string; error: string }>;
    /** True when the apology broadcast went out (cant_be_seated path only). */
    emailSent?: boolean;
  }>;

  // PATCH /api/admin/tables/:id/lock
  export const ToggleTableLockRequestSchema = z.object({
    isActive: z.boolean(),
  });

  export type ToggleTableLockRequest = z.infer<typeof ToggleTableLockRequestSchema>;
  export type ToggleTableLockResponse = ApiResponse<void>;

  // POST /api/admin/tables
  export const CreateTableRequestSchema = z.object({
    eventTimeId: z.string(),
    restaurantId: z.string(),
    capacity: z.number().min(1),
    reservationName: z.string().optional(),
  });

  export type CreateTableRequest = z.infer<typeof CreateTableRequestSchema>;
  export type CreateTableResponse = ApiResponse<AvailableTable>;

  // PATCH /api/admin/tables/:id
  export const UpdateTableRequestSchema = z.object({
    reservationName: z.string().optional(),
    capacity: z.number().min(1).optional(),
    restaurantId: z.string().optional(),
    reservationDone: z.string().nullable().optional(),
  });

  export type UpdateTableRequest = z.infer<typeof UpdateTableRequestSchema>;
  export type UpdateTableResponse = ApiResponse<void>;

  // DELETE /api/admin/tables/:id
  export type DeleteTableResponse = ApiResponse<void>;

  // GET /api/admin/audit-logs — append-only audit trail for table/booking
  // admin actions. Returns rows newest-first; cursor-paginate via `before`.
  export const AuditLogEntrySchema = z.object({
    id: z.string(),
    createdAt: z.string(), // ISO timestamp
    action: z.string(),
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
    subjectUserId: z.string().nullable(),
    subjectName: z.string().nullable(),
    bookingId: z.string().nullable(),
    availableTableId: z.string().nullable(),
    tableReservationName: z.string().nullable(),
    restaurantName: z.string().nullable(),
    eventTimeId: z.string().nullable(),
    eventDate: z.string().nullable(),
    eventTime: z.string().nullable(),
    metadata: z.any().nullable(),
  });
  export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

  export const GetAuditLogsRequestSchema = z.object({
    eventTimeId: z.string().optional(),
    bookingId: z.string().optional(),
    availableTableId: z.string().optional(),
    subjectUserId: z.string().optional(),
    actorUserId: z.string().optional(),
    action: z.string().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    before: z.string().optional(), // ISO timestamp — return rows created strictly before this
  });
  export type GetAuditLogsRequest = z.infer<typeof GetAuditLogsRequestSchema>;

  export type GetAuditLogsResponse = ApiResponse<{
    entries: AuditLogEntry[];
    nextBefore: string | null;
  }>;

  // GET /api/admin/users
  export type GetUsersResponse = ApiResponse<any[]>;

  // GET /api/admin/users/detailed - Users list with booking/payment counts
  export const AdminUserBookingSchema = z.object({
    id: z.string(),
    status: z.enum(['pending', 'active', 'cancelled', 'cant_be_seated']),
    eventTimeId: z.string(),
    eventDate: z.string(),
    eventTime: z.string(),
    // IANA zone of the event's city, and the dinner start as a real instant —
    // eventDate/eventTime are naive wall-clock strings on their own.
    eventTimezone: z.string().nullable(),
    eventStartsAt: z.date().nullable(),
    neighborhoodId: z.string().nullable(),
    neighborhoodName: z.string().nullable(),
    tableId: z.string().nullable(),
    restaurantName: z.string().nullable(),
    restaurantAddress: z.string().nullable(),
    cancellationReason: z.string().nullable(),
    cancelledAt: z.date().nullable(),
    specialRequests: z.string().nullable(),
    createdAt: z.date(),
  });

  export const AdminUserPaymentSchema = z.object({
    id: z.string(),
    amount: z.string(),
    currency: z.string(),
    status: z.string(),
    type: z.string(),
    paymentProcessor: z.enum(["STRIPE", "APP_STORE", "PLAY_STORE"]).default("STRIPE"),
    isRenewal: z.boolean().nullable().optional(),
    createdAt: z.date(),
  });

  export const AdminUserEntitlementSchema = z.object({
    id: z.string(),
    type: z.enum(['subscription', 'one_time']),
    status: z.string(),
    begin: z.date().nullable(),
    end: z.date().nullable(),
    renewsAt: z.date().nullable(),
    revenuecatTransactionId: z.string().nullable().optional(),
    stripeSubscriptionId: z.string().nullable().optional(),
    stripePaymentId: z.string().nullable().optional(),
  });

  export const AdminUserDeviceSchema = z.object({
    id: z.string(),
    platform: z.string().nullable(),
    deviceInfo: z.string().nullable(),
    lastSeen: z.date(),
    createdAt: z.date(),
  });

  // Staff roles that can be assigned to a user. When set, the role is
  // replicated into the user's Firebase custom claims (see auth middleware).
  export const UserRoleSchema = z.enum(['admin', 'moderator', 'analytics', 'tester']);
  export type UserRole = z.infer<typeof UserRoleSchema>;

  export const AdminUserSummarySchema = z.object({
    id: z.string(),
    username: z.string(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    instagram: z.string().nullable(),
    city: z.string().nullable(),
    profileImageUrl: z.string().nullable(),
    // Optional staff role (null for regular users).
    role: UserRoleSchema.nullable(),
    createdAt: z.date(),
    quizCompletedAt: z.date().nullable(),
    // Aggregate counts
    totalBookings: z.number(),
    activeBookings: z.number(),
    cancelledBookings: z.number(),
    // Subscription status
    hasActiveSubscription: z.boolean(),
    subscriptionStatus: z.string().nullable(),
  });

  export const AdminUserPostDinnerFeedbackSchema = z.object({
    id: z.string(),
    bookingId: z.string().nullable(),
    overallExperience: z.number().nullable(),
    feltWelcomed: z.string().nullable(),
    restaurantSelection: z.string().nullable(),
    futureAttendance: z.string().nullable(),
    recommendationScore: z.number().nullable(),
    notes: z.string().nullable(),
    eventDate: z.string().nullable(),
    eventTime: z.string().nullable(),
    restaurantName: z.string().nullable(),
    createdAt: z.coerce.date(),
  });

  export const AdminUserReviewSchema = z.object({
    id: z.string(),
    bookingId: z.string().nullable(),
    rating: z.number().nullable(),
    comment: z.string().nullable(),
    eventDate: z.string().nullable(),
    eventTime: z.string().nullable(),
    restaurantName: z.string().nullable(),
    createdAt: z.coerce.date(),
  });

  export const AdminUserGeneralFeedbackSchema = z.object({
    id: z.string(),
    feedback: z.string(),
    createdAt: z.coerce.date(),
  });

  export const AdminUserDetailSchema = AdminUserSummarySchema.extend({
    // Stripe customer ID for linking to Stripe dashboard
    stripeCustomerId: z.string().nullable(),
    // Full quiz answers as parsed JSON
    quizAnswers: z.record(z.string(), z.any()).nullable(),
    // Detailed booking history
    bookings: z.array(AdminUserBookingSchema),
    // Payment history
    payments: z.array(AdminUserPaymentSchema),
    // Entitlements
    entitlements: z.array(AdminUserEntitlementSchema),
    // Registered devices (push / FCM)
    devices: z.array(AdminUserDeviceSchema),
    // Feedback & survey responses
    postDinnerFeedback: z.array(AdminUserPostDinnerFeedbackSchema),
    reviews: z.array(AdminUserReviewSchema),
    generalFeedback: z.array(AdminUserGeneralFeedbackSchema),
  });

  export type AdminUserDevice = z.infer<typeof AdminUserDeviceSchema>;
  export type AdminUserBooking = z.infer<typeof AdminUserBookingSchema>;
  export type AdminUserPayment = z.infer<typeof AdminUserPaymentSchema>;
  export type AdminUserEntitlement = z.infer<typeof AdminUserEntitlementSchema>;
  export type AdminUserPostDinnerFeedback = z.infer<typeof AdminUserPostDinnerFeedbackSchema>;
  export type AdminUserReview = z.infer<typeof AdminUserReviewSchema>;
  export type AdminUserGeneralFeedback = z.infer<typeof AdminUserGeneralFeedbackSchema>;
  export type AdminUserSummary = z.infer<typeof AdminUserSummarySchema>;
  export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

  // GET /api/admin/users/detailed - Paginated users list
  export const GetUsersDetailedRequestSchema = z.object({
    page: z.number().min(1).default(1),
    limit: z.number().min(1).max(100).default(25),
    search: z.string().optional(),
    sortField: z.enum(['createdAt', 'firstName', 'totalBookings', 'hasActiveSubscription']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    subscriptionFilter: z.enum(['all', 'active', 'none']).default('all'),
    // 'staff' restricts the list to users with a non-empty role.
    roleFilter: z.enum(['all', 'staff']).default('all'),
  });

  // PATCH /api/admin/users/:id/role - set or clear a user's staff role.
  // role: null clears the role (and removes the Firebase claim).
  export const UpdateUserRoleRequestSchema = z.object({
    role: UserRoleSchema.nullable(),
  });
  export type UpdateUserRoleRequest = z.infer<typeof UpdateUserRoleRequestSchema>;
  export type UpdateUserRoleResponse = ApiResponse<{ role: UserRole | null }>;

  export const PaginatedUsersDataSchema = z.object({
    users: z.array(AdminUserSummarySchema),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
    // Summary stats for the entire dataset (not just current page)
    stats: z.object({
      totalUsers: z.number(),
      usersWithSubscription: z.number(),
      usersWithBookings: z.number(),
      quizCompleted: z.number(),
    }),
  });

  export type GetUsersDetailedRequest = z.infer<typeof GetUsersDetailedRequestSchema>;
  export type PaginatedUsersData = z.infer<typeof PaginatedUsersDataSchema>;
  export type GetUsersDetailedResponse = ApiResponse<PaginatedUsersData>;

  // GET /api/admin/users/:id - Single user with full details
  export type GetUserDetailResponse = ApiResponse<AdminUserDetail>;

  // PATCH /api/admin/bookings/:id
  export const UpdateBookingRequestSchema = z.object({
    status: z.enum(['pending', 'active', 'cancelled', 'cant_be_seated']).optional(),
    neighborhoodId: z.string().optional(),
    eventTimeId: z.string().optional(),
  });

  export type UpdateBookingRequest = z.infer<typeof UpdateBookingRequestSchema>;
  export type UpdateBookingResponse = ApiResponse<void>;

  // POST /api/admin/users/:id/bookings - Create a booking for a user (admin only)
  export const CreateAdminBookingRequestSchema = z.object({
    eventTimeId: z.string(),
    neighborhoodId: z.string(),
    status: z.enum(['pending', 'active']).default('active'),
  });

  export type CreateAdminBookingRequest = z.infer<typeof CreateAdminBookingRequestSchema>;
  export type CreateAdminBookingResponse = ApiResponse<AdminUserBooking>;

  // POST /api/admin/users/:id/extend-subscription — push a Stripe subscriber's
  // next billing date out by `days`. Stacks with any previous extension.
  export const ExtendSubscriptionRequestSchema = z.object({
    days: z.number().int().min(1).max(365).default(7),
  });

  export type ExtendSubscriptionRequest = z.infer<typeof ExtendSubscriptionRequestSchema>;

  export const ExtendSubscriptionResultSchema = z.object({
    extended: z.boolean(),
    days: z.number(),
    /** Why the extension didn't happen, when `extended` is false. */
    reason: z.string().optional(),
    entitlementId: z.string().optional(),
    stripeSubscriptionId: z.string().optional(),
    previousRenewalAt: z.string().nullable().optional(),
    newRenewalAt: z.string().nullable().optional(),
  });

  export type ExtendSubscriptionResult = z.infer<typeof ExtendSubscriptionResultSchema>;
  export type ExtendSubscriptionResponse = ApiResponse<ExtendSubscriptionResult>;

  // POST /api/admin/users/merge - Merge source user's data into target user (admin only). Matches accountMigration merge options; all default true.
  export const MergeUsersRequestSchema = z.object({
    sourceUserId: z.string(),
    targetUserId: z.string(),
    mergeBookings: z.boolean().default(true),
    mergeEntitlements: z.boolean().default(true),
    mergePayments: z.boolean().default(true),
    mergeReviews: z.boolean().default(true),
    mergePostDinnerFeedback: z.boolean().default(true),
    mergeUserFeedback: z.boolean().default(true),
    mergeUserConnections: z.boolean().default(true),
    mergeMutualConnections: z.boolean().default(true),
    mergeUserDevices: z.boolean().default(true),
    mergePollResponses: z.boolean().default(true),
    mergeReferrals: z.boolean().default(true),
    mergeCustomers: z.boolean().default(true),
    mergeRestaurantExclusions: z.boolean().default(true),
    mergeAutomationLogs: z.boolean().default(true),
    mergeFirestoreConversations: z.boolean().default(true),
    mergeCustomerIo: z.boolean().default(true),
    mergeProfile: z.boolean().default(false),
  });

  export type MergeUsersRequest = z.infer<typeof MergeUsersRequestSchema>;
  export type MergeUsersResponse = ApiResponse<{ merged: string[] }>;

  // POST /api/admin/users/history
  export const UserHistorySchema = z.object({
    previousRestaurants: z.array(z.object({
      id: z.string(),
      name: z.string(),
      visitedAt: z.string(),
    })),
    previousTableMates: z.array(z.object({
      id: z.string(),
      name: z.string(),
      metAt: z.string(),
      /**
       * Mutual-contact-sharing status between the user this history belongs to
       * and the tablemate. "mutual" when both shared contact info, "only_them"
       * when only the tablemate did, "only_me" when only the history user did,
       * "none" otherwise.
       */
      connection: z.enum(["mutual", "only_them", "only_me", "none"]),
    })),
    recentFeedback: z.array(z.object({
      id: z.string(),
      restaurantName: z.string().nullable(),
      overallExperience: z.number().nullable(),
      notes: z.string().nullable(),
      createdAt: z.string(),
    })).optional(),
  });

  export type UserHistory = z.infer<typeof UserHistorySchema>;
  export type GetUserHistoryResponse = ApiResponse<Record<string, UserHistory>>;

  // Internal staff notes on a user. Admin/MCP-only — never returned by any
  // user-facing endpoint.
  export const UserNoteSchema = z.object({
    id: z.string(),
    userId: z.string(),
    note: z.string(),
    createdBy: z.string(),
    createdAt: z.string(),
  });

  export type UserNote = z.infer<typeof UserNoteSchema>;

  // GET /api/admin/users/:userId/notes
  export type GetUserNotesResponse = ApiResponse<UserNote[]>;

  // POST /api/admin/users/:userId/notes
  export const CreateUserNoteRequestSchema = z.object({
    note: z.string().min(1).max(2000),
  });
  export type CreateUserNoteRequest = z.infer<typeof CreateUserNoteRequestSchema>;
  export type CreateUserNoteResponse = ApiResponse<UserNote>;

  // DELETE /api/admin/user-notes/:noteId
  export type DeleteUserNoteResponse = ApiResponse<void>;

  // GET /api/admin/dashboard/metrics
  export const BookingsTimeSeriesSchema = z.object({
    date: z.string(),
    count: z.number(),
  });

  export const DashboardMetricsSchema = z.object({
    totalBookings: z.number(),
    activeBookings: z.number(),
    payingCustomers: z.number(),
    recurringRevenue: z.number(),
    bookingsTimeSeries: z.array(BookingsTimeSeriesSchema),
  });

  export type BookingsTimeSeries = z.infer<typeof BookingsTimeSeriesSchema>;
  export type DashboardMetrics = z.infer<typeof DashboardMetricsSchema>;
  export type GetDashboardMetricsResponse = ApiResponse<DashboardMetrics>;

  // GET /api/admin/dashboard/stats - Enhanced dashboard stats
  export const CityUserStatsSchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    totalUsers: z.number(),
    activeSubscribers: z.number(),
    paidUsers: z.number(),
    signupsLastDay: z.number(),
    signupsLastWeek: z.number(),
    signupsLastMonth: z.number(),
  });

  export const BookingsPerCityEventSchema = z.object({
    date: z.string(),
    cityId: z.string(),
    cityName: z.string(),
    count: z.number(),
  });

  export const SignupsPerDaySchema = z.object({
    date: z.string(),
    count: z.number(),
  });

  export const NextEventByCitySchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    emoji: z.string(),
    timezone: z.string(),
    date: z.string(),
    time: z.string(),
    startsAt: z.string(), // ISO UTC timestamp
  });

  export const DashboardStatsSchema = z.object({
    usersPerCity: z.array(CityUserStatsSchema),
    bookingsPerCityEvent: z.array(BookingsPerCityEventSchema),
    signupsPerDay: z.array(SignupsPerDaySchema),
    nextEventsByCity: z.array(NextEventByCitySchema),
  });

  export type CityUserStats = z.infer<typeof CityUserStatsSchema>;
  export type BookingsPerCityEvent = z.infer<typeof BookingsPerCityEventSchema>;
  export type SignupsPerDay = z.infer<typeof SignupsPerDaySchema>;
  export type NextEventByCity = z.infer<typeof NextEventByCitySchema>;
  export type DashboardStats = z.infer<typeof DashboardStatsSchema>;
  export type GetDashboardStatsResponse = ApiResponse<DashboardStats>;

  // GET /api/admin/payments
  export const PaymentSchema = z.object({
    id: z.string(),
    stripeCustomerId: z.string().nullable(),
    stripeSessionId: z.string(),
    stripePaymentIntentId: z.string().nullable(),
    amount: z.string(),
    amountUsd: z.string().nullable(),
    currency: z.string(),
    status: z.string(),
    type: z.string(),
    paymentProcessor: z.enum(["STRIPE", "APP_STORE", "PLAY_STORE"]).default("STRIPE"),
    sandbox: z.boolean().default(false),
    isRenewal: z.boolean().nullable(),
    planId: z.string().nullable(),
    refundId: z.string().nullable(),
    refundAmount: z.string().nullable(),
    refundedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    user: z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
    }).nullable(),
  });

  export type Payment = z.infer<typeof PaymentSchema>;

  // GET /api/admin/payments - Paginated
  export const GetPaymentsRequestSchema = z.object({
    page: z.number().min(1).default(1),
    limit: z.number().min(1).max(100).default(25),
    status: z.enum(['all', 'succeeded', 'pending', 'failed', 'refunded']).default('all'),
  });

  export const PaginatedPaymentsDataSchema = z.object({
    payments: z.array(PaymentSchema),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
    stats: z.object({
      totalRevenue: z.number(),
      totalRefunds: z.number(),
      totalPayments: z.number(),
      successfulPayments: z.number(),
    }),
  });

  export type GetPaymentsRequest = z.infer<typeof GetPaymentsRequestSchema>;
  export type PaginatedPaymentsData = z.infer<typeof PaginatedPaymentsDataSchema>;
  export type GetPaymentsResponse = ApiResponse<PaginatedPaymentsData>;

  // GET /api/admin/payments/charts
  export const DailyRevenuePointSchema = z.object({
    date: z.string(),
    STRIPE: z.number(),
    APP_STORE: z.number(),
    PLAY_STORE: z.number(),
    // Renewal-only slice per source. Subset of the per-source total above.
    STRIPE_RENEWAL: z.number(),
    APP_STORE_RENEWAL: z.number(),
    PLAY_STORE_RENEWAL: z.number(),
    Total: z.number(),
  });

  export const TimeOfDayPointSchema = z.object({
    minute: z.number(),
    label: z.string(),
    today: z.number(),
    yesterday: z.number(),
    sevenDaysAgo: z.number(),
  });

  export const PaymentChartsDataSchema = z.object({
    dailyRevenue: z.array(DailyRevenuePointSchema),
    timeOfDay: z.object({
      data: z.array(TimeOfDayPointSchema),
      todayStr: z.string(),
      yesterdayStr: z.string(),
      sevenDaysAgoStr: z.string(),
    }),
    sources: z.array(z.string()),
    granularity: z.enum(["day", "week", "month"]).default("day"),
  });

  export type DailyRevenuePoint = z.infer<typeof DailyRevenuePointSchema>;
  export type TimeOfDayPoint = z.infer<typeof TimeOfDayPointSchema>;
  export type PaymentChartsData = z.infer<typeof PaymentChartsDataSchema>;
  export type GetPaymentChartsResponse = ApiResponse<PaymentChartsData>;

  // GET /api/admin/referrals - Paginated
  export const ReferralSchema = z.object({
    id: z.string(),
    referrer: z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      stripeCustomerId: z.string().nullable(),
    }),
    referredUser: z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      stripeCustomerId: z.string().nullable(),
    }),
    status: z.enum(['pending', 'used', 'rewarded']),
    createdAt: z.date(),
  });

  export const GetReferralsRequestSchema = z.object({
    page: z.number().min(1).default(1),
    limit: z.number().min(1).max(100).default(25),
    status: z.enum(['all', 'pending', 'used', 'rewarded']).default('all'),
  });

  export const PaginatedReferralsDataSchema = z.object({
    referrals: z.array(ReferralSchema),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
    stats: z.object({
      totalReferrals: z.number(),
      pendingReferrals: z.number(),
      usedReferrals: z.number(),
      rewardedReferrals: z.number(),
    }),
  });

  export type Referral = z.infer<typeof ReferralSchema>;
  export type GetReferralsRequest = z.infer<typeof GetReferralsRequestSchema>;
  export type PaginatedReferralsData = z.infer<typeof PaginatedReferralsDataSchema>;
  export type GetReferralsResponse = ApiResponse<PaginatedReferralsData>;

  // GET /api/admin/feedback
  export const PostDinnerFeedbackSchema = z.object({
    id: z.string(),
    userId: z.string(),
    bookingId: z.string(),
    overallExperience: z.number().nullable(),
    feltWelcomed: z.string().nullable(),
    locationTiming: z.string().nullable(),
    locationTimingReason: z.string().nullable(),
    restaurantSelection: z.string().nullable(),
    futureAttendance: z.string().nullable(),
    recommendationScore: z.number().nullable(),
    notes: z.string().nullable(),
    createdAt: z.date(),
    user: z.object({
      firstName: z.string(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      instagram: z.string().nullable(),
    }).nullable(),
    booking: z.object({
      eventTime: z.object({
        date: z.string(),
        time: z.string(),
      }),
      neighborhoodName: z.string().nullable(),
      restaurantName: z.string().nullable(),
    }).nullable(),
  });

  export const ReviewSchema = z.object({
    id: z.string(),
    userId: z.string(),
    bookingId: z.string(),
    rating: z.number().nullable(),
    comment: z.string().nullable(),
    createdAt: z.date(),
    user: z.object({
      firstName: z.string(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      instagram: z.string().nullable(),
    }).nullable(),
  });

  export const UserFeedbackSchema = z.object({
    id: z.string(),
    userId: z.string(),
    feedback: z.string(),
    createdAt: z.date(),
    user: z.object({
      firstName: z.string(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      instagram: z.string().nullable(),
    }).nullable(),
  });

  export const AllFeedbackSchema = z.object({
    postDinner: z.array(PostDinnerFeedbackSchema),
    reviews: z.array(ReviewSchema),
    general: z.array(UserFeedbackSchema),
  });

  export type PostDinnerFeedback = z.infer<typeof PostDinnerFeedbackSchema>;
  export type Review = z.infer<typeof ReviewSchema>;
  export type UserFeedback = z.infer<typeof UserFeedbackSchema>;
  export type AllFeedback = z.infer<typeof AllFeedbackSchema>;
  export type GetFeedbackResponse = ApiResponse<AllFeedback>;

  // Query filters shared by GET /api/admin/feedback and /api/admin/feedback/stats.
  // Dates are inclusive YYYY-MM-DD strings. The list filters on when the
  // feedback was submitted; the stats chart filters on the event date.
  // Omitting a field means "no restriction".
  export const FeedbackEventTypeSchema = z.enum(["dinner", "brunch", "drinks", "coffee"]);
  export type FeedbackEventType = z.infer<typeof FeedbackEventTypeSchema>;
  export const FeedbackFiltersQuerySchema = z.object({
    eventType: FeedbackEventTypeSchema.optional(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD").optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD").optional(),
  });
  export type FeedbackFiltersQuery = z.infer<typeof FeedbackFiltersQuerySchema>;

  // GET /api/admin/feedback/stats
  export const FeedbackStatSchema = z.object({
    eventDate: z.string(),
    avgExperience: z.number().nullable(),
    avgNps: z.number().nullable(),
    responseCount: z.number(),
    // Share (0-100) of respondents who answered the future-attendance question
    // with "yes"; null when nobody on that date answered it.
    futureAttendancePct: z.number().nullable(),
    futureAttendanceResponses: z.number(),
  });
  export type FeedbackStat = z.infer<typeof FeedbackStatSchema>;
  export type GetFeedbackStatsResponse = ApiResponse<FeedbackStat[]>;

  // GET /api/admin/restaurants/stats
  export const RestaurantStatsSchema = z.object({
    restaurantId: z.string(),
    avgNps: z.number().nullable(),
    avgExperience: z.number().nullable(),
    responseCount: z.number(),
    ratingDistribution: z.object({
      perfect: z.number(),
      good: z.number(),
      okay: z.number(),
      notGreat: z.number(),
      poor: z.number(),
    }),
  });
  export type RestaurantStats = z.infer<typeof RestaurantStatsSchema>;
  export type GetRestaurantStatsResponse = ApiResponse<Record<string, RestaurantStats>>;

  // GET /api/admin/neighborhoods
  export const CitySchema = z.object({
    id: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    createdAt: z.date(),
  });

  export const NeighborhoodSchema = z.object({
    id: z.string(),
    name: z.string(),
    cityId: z.string(),
    cityName: z.string().nullable(),
    isActive: z.boolean(),
    launchedAfter: z.string().nullable(),
    freeUntil: z.string().nullable(),
    createdAt: z.date(),
  });

  export const RestaurantNeighborhoodSchema = z.object({
    id: z.string(),
    name: z.string(),
    address: z.string(),
    neighborhoodId: z.string(),
    neighborhoodName: z.string().nullable(),
    type: z.string().default("restaurant"),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    googleMapsLink: z.string().nullable(),
    bookingLink: z.string().nullable(),
    googlePlaceId: z.string().nullable(),
    tags: z.array(z.string()).nullable(),
    isActive: z.boolean(),
    createdAt: z.date(),
  });

  export const AllNeighborhoodsDataSchema = z.object({
    cities: z.array(CitySchema),
    neighborhoods: z.array(NeighborhoodSchema),
    restaurants: z.array(RestaurantNeighborhoodSchema),
  });

  export type City = z.infer<typeof CitySchema>;
  export type NeighborhoodData = z.infer<typeof NeighborhoodSchema>;
  export type RestaurantNeighborhood = z.infer<typeof RestaurantNeighborhoodSchema>;
  export type AllNeighborhoodsData = z.infer<typeof AllNeighborhoodsDataSchema>;
  export type GetNeighborhoodsDataResponse = ApiResponse<AllNeighborhoodsData>;

  // GET /api/admin/neighborhoods/restaurants - Paginated
  export const GetRestaurantsRequestSchema = z.object({
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(1).max(200).default(50),
    search: z.string().optional(),
    cityId: z.string().optional(),
    neighborhoodId: z.string().optional(),
    type: z.string().optional(),
    isActive: z.enum(["true", "false"]).optional(),
  });

  export const PaginatedRestaurantsDataSchema = z.object({
    restaurants: z.array(RestaurantNeighborhoodSchema),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  });

  export type GetRestaurantsRequest = z.infer<typeof GetRestaurantsRequestSchema>;
  export type PaginatedRestaurantsData = z.infer<typeof PaginatedRestaurantsDataSchema>;
  export type GetRestaurantsResponse = ApiResponse<PaginatedRestaurantsData>;

  // POST /api/admin/neighborhoods/cities
  export const CreateCityRequestSchema = z.object({
    id: z.string(),
    name: z.string(),
  });

  export type CreateCityRequest = z.infer<typeof CreateCityRequestSchema>;
  export type CreateCityResponse = ApiResponse<City>;

  // POST /api/admin/neighborhoods/neighborhoods
  export const CreateNeighborhoodRequestSchema = z.object({
    id: z.string(),
    name: z.string(),
    cityId: z.string(),
    launchedAfter: z.string().optional(),
    freeUntil: z.string().optional(),
  });

  export type CreateNeighborhoodRequest = z.infer<typeof CreateNeighborhoodRequestSchema>;
  export type CreateNeighborhoodResponse = ApiResponse<NeighborhoodData>;

  // POST /api/admin/neighborhoods/restaurants
  export const CreateRestaurantRequestSchema = z.object({
    name: z.string(),
    address: z.string(),
    neighborhoodId: z.string(),
    type: z.string().default("restaurant").optional(), // restaurant, bar, cafe, etc.
    lat: z.number().optional(),
    lng: z.number().optional(),
    phone: z.string().optional(),
    website: z.string().optional(),
    googleMapsLink: z.string().optional(),
    bookingLink: z.string().optional(),
    googlePlaceId: z.string().optional(),
    tags: z.array(z.string()).optional(),
  });

  export type CreateRestaurantRequest = z.infer<typeof CreateRestaurantRequestSchema>;
  export type CreateRestaurantResponse = ApiResponse<RestaurantNeighborhood>;

  // PATCH /api/admin/neighborhoods/cities/:id
  export const UpdateCityRequestSchema = z.object({
    name: z.string().optional(),
    isActive: z.boolean().optional(),
  });

  export type UpdateCityRequest = z.infer<typeof UpdateCityRequestSchema>;
  export type UpdateCityResponse = ApiResponse<City>;

  // PATCH /api/admin/neighborhoods/neighborhoods/:id
  export const UpdateNeighborhoodRequestSchema = z.object({
    name: z.string().optional(),
    cityId: z.string().optional(),
    isActive: z.boolean().optional(),
    launchedAfter: z.string().nullable().optional(),
    freeUntil: z.string().nullable().optional(),
  });

  export type UpdateNeighborhoodRequest = z.infer<typeof UpdateNeighborhoodRequestSchema>;
  export type UpdateNeighborhoodResponse = ApiResponse<NeighborhoodData>;

  // PATCH /api/admin/neighborhoods/restaurants/:id
  export const UpdateRestaurantRequestSchema = z.object({
    name: z.string().optional(),
    address: z.string().optional(),
    neighborhoodId: z.string().optional(),
    type: z.string().optional(), // restaurant, bar, cafe, etc.
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
    phone: z.string().optional(),
    website: z.string().optional(),
    googleMapsLink: z.string().optional(),
    bookingLink: z.string().optional(),
    googlePlaceId: z.string().optional(),
    tags: z.array(z.string()).optional(),
    isActive: z.boolean().optional(),
  });

  export type UpdateRestaurantRequest = z.infer<typeof UpdateRestaurantRequestSchema>;
  export type UpdateRestaurantResponse = ApiResponse<RestaurantNeighborhood>;

  // GET /api/admin/google-maps/search - Search Google Maps for restaurants
  export const GoogleMapsSearchRequestSchema = z.object({
    query: z.string(),
    lat: z.number().optional(),
    lng: z.number().optional(),
  });

  export const GoogleMapsRestaurantSchema = z.object({
    placeId: z.string(),
    name: z.string(),
    address: z.string(),
    phone: z.string().optional(),
    website: z.string().optional(),
    googleMapsUri: z.string(),
    priceLevel: z.string().optional(),
    rating: z.number().optional(),
    userRatingCount: z.number().optional(),
    types: z.array(z.string()),
    primaryType: z.string().optional(),
    tags: z.array(z.string()),
    lat: z.number().optional(),
    lng: z.number().optional(),
    /** Editorial summary / short description from Google. */
    description: z.string().optional(),
    /** Places API v1 photo resource name (e.g. "places/<id>/photos/<id>"). Client builds the media URL. */
    photoName: z.string().optional(),
  });

  export const GoogleMapsSearchDataSchema = z.object({
    restaurants: z.array(GoogleMapsRestaurantSchema),
  });

  export type GoogleMapsSearchRequest = z.infer<typeof GoogleMapsSearchRequestSchema>;
  export type GoogleMapsRestaurant = z.infer<typeof GoogleMapsRestaurantSchema>;
  export type GoogleMapsSearchData = z.infer<typeof GoogleMapsSearchDataSchema>;
  export type GoogleMapsSearchResponse = ApiResponse<GoogleMapsSearchData>;

  // GET /api/admin/google-maps/place/:placeId - Get place details
  export type GoogleMapsPlaceDetailsResponse = ApiResponse<GoogleMapsRestaurant>;

  // POST /api/admin/chats/close-out — create an after-dinner "close out" chat
  // for everyone assigned to a table in the given neighborhood on the given event.
  export const CreateCloseOutChatRequestSchema = z.object({
    eventTimeId: z.string().min(1),
    neighborhoodId: z.string().min(1),
    barRestaurantId: z.string().min(1),
    meetingTime: z.string().min(1), // ISO 8601 datetime
    name: z.string().optional(),
    description: z.string().optional(),
  });
  export type CreateCloseOutChatRequest = z.infer<typeof CreateCloseOutChatRequestSchema>;
  export type CreateCloseOutChatResponse = ApiResponse<{
    conversationId: string;
    participantCount: number;
  }>;

  // GET /api/admin/events/:eventTimeId/close-out-bars — list per-neighborhood
  // close-out bar configuration plus distance metrics for candidate bars.
  export const CloseOutBarCandidateSchema = z.object({
    barRestaurantId: z.string(),
    barName: z.string(),
    barAddress: z.string().nullable(),
    barWebsite: z.string().nullable(),
    barGoogleMapsLink: z.string().nullable(),
    barPhone: z.string().nullable(),
    avgDistanceKm: z.number().nullable(),
    maxDistanceKm: z.number().nullable(),
  });
  export type CloseOutBarCandidate = z.infer<typeof CloseOutBarCandidateSchema>;

  export const CloseOutBarNeighborhoodSchema = z.object({
    neighborhoodId: z.string(),
    neighborhoodName: z.string(),
    cityId: z.string().nullable(),
    cityName: z.string().nullable(),
    tableCount: z.number(),
    selectedBarRestaurantId: z.string().nullable(),
    candidates: z.array(CloseOutBarCandidateSchema),
    chatState: z.enum(['scheduled', 'open', 'closed']).nullable(),
    conversationId: z.string().nullable(),
  });
  export type CloseOutBarNeighborhood = z.infer<typeof CloseOutBarNeighborhoodSchema>;

  export type CloseOutBarConfigResponse = ApiResponse<{
    neighborhoods: CloseOutBarNeighborhood[];
  }>;

  // PUT /api/admin/events/:eventTimeId/close-out-bars/:neighborhoodId — set or
  // clear the close-out bar for a (eventTime, neighborhood) pair. Pass
  // barRestaurantId: null to clear.
  export const SetCloseOutBarRequestSchema = z.object({
    barRestaurantId: z.string().nullable(),
  });
  export type SetCloseOutBarRequest = z.infer<typeof SetCloseOutBarRequestSchema>;
  export type SetCloseOutBarResponse = ApiResponse<{ neighborhoodId: string; barRestaurantId: string | null }>;

  // POST /api/admin/events/:eventTimeId/close-out-bars/:neighborhoodId/open-chat
  // POST /api/admin/events/:eventTimeId/close-out-bars/:neighborhoodId/close-chat
  // Lets admins manually open or close the close-out chat. Opening creates the
  // group conversation (re-using existing one if already opened); closing just
  // flips the config state so the user-facing card hides the link and the
  // automation won't re-open it.
  export type ToggleCloseOutChatResponse = ApiResponse<{
    neighborhoodId: string;
    chatState: 'scheduled' | 'open' | 'closed';
    conversationId: string | null;
  }>;

  // POST /api/admin/events/:eventTimeId/close-out-bars/auto-assign — assign the
  // closest bar (by avg distance to currently-assigned restaurants) to every
  // neighborhood with at least N tables.
  export type AutoAssignCloseOutBarsResponse = ApiResponse<{
    assigned: Array<{ neighborhoodId: string; barRestaurantId: string }>;
    skipped: Array<{ neighborhoodId: string; reason: string }>;
  }>;

  // POST /api/admin/chats/:conversationId/close-out/resend-broadcast — re-send
  // the "Close out chat opened" broadcast (broadcast 65) to every participant
  // of an existing close-out chat. Does not create or modify the chat.
  export type ResendCloseOutChatBroadcastResponse = ApiResponse<{
    participantCount: number;
  }>;

  // ========== Event Ops Automation (docs/EVENT_OPS_AUTOMATION.md) ==========

  export const AgentTaskTypeSchema = z.enum([
    'match.initial',
    'match.reoptimize',
    'match.finalize',
    'reserve.book',
    'reserve.verify',
    'reserve.release',
    'venue.research',
  ]);
  export type AgentTaskType = z.infer<typeof AgentTaskTypeSchema>;

  // Tasks that run once per EVENT (run.neighborhoodId null) instead of per
  // (event × neighborhood). Single source of truth for server (playbooks.ts
  // builds EVENT_SCOPED_TASKS from it) and dashboard (event-level stage chips).
  // The audit and the release are event-wide: both work ONE OpenTable account
  // across the whole event (the release still times each neighborhood against
  // its own local dinner — see scheduler.ts). Booking is per-neighborhood — it
  // fires at T-48h of that neighborhood's OWN local dinner.
  export const EVENT_SCOPED_AGENT_TASK_TYPES: AgentTaskType[] = ['reserve.verify', 'reserve.release'];

  // Which event types involve restaurant reservations at all. Single source
  // of truth for the reservation pipeline: reserve.* runs are refused for
  // other types (queue guard), the demand-driven reservation scheduler skips
  // them, locking is NOT reservation-gated for them, and the dashboard hides
  // reservation stages/health.
  export const EVENT_TYPES_REQUIRING_RESERVATIONS = ['dinner', 'brunch'] as const;

  export function eventTypeRequiresReservations(eventType: string | null | undefined): boolean {
    // Unknown/missing types default to requiring reservations — the safe side.
    return (EVENT_TYPES_REQUIRING_RESERVATIONS as readonly string[]).includes(eventType ?? 'dinner');
  }

  export const AgentRunStatusSchema = z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']);
  export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>;

  export const AgentRunSchema = z.object({
    id: z.string(),
    taskType: z.string(),
    eventTimeId: z.string().nullable(), // null = no event scope (triage.* runs)
    neighborhoodId: z.string().nullable(), // null = event-wide run
    neighborhoodName: z.string().nullable(),
    milestoneKey: z.string().nullable(),
    trigger: z.string(),
    status: AgentRunStatusSchema,
    attempt: z.number(),
    rerunRequested: z.boolean(),
    scheduledFor: z.string(), // ISO timestamp
    claimedAt: z.string().nullable(),
    lastActivityAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    summary: z.string().nullable(),
    metrics: z.any().nullable(), // { inputTokens, outputTokens, costUsd, durationMs, toolCalls }
    error: z.string().nullable(),
    createdAt: z.string(),
  });
  export type AgentRun = z.infer<typeof AgentRunSchema>;

  export const AgentRunEventSchema = z.object({
    id: z.string(),
    runId: z.string(),
    seq: z.number(),
    type: z.string(), // progress | decision | observation
    title: z.string(),
    detail: z.string().nullable(), // markdown
    data: z.any().nullable(),
    createdAt: z.string(),
  });
  export type AgentRunEvent = z.infer<typeof AgentRunEventSchema>;

  export const AgentDecisionSchema = z.object({
    id: z.string(),
    runId: z.string().nullable(),
    eventTimeId: z.string(),
    neighborhoodId: z.string().nullable(), // null = raised by an event-wide run
    neighborhoodName: z.string().nullable(),
    kind: z.enum(['approval', 'question', 'escalation']),
    title: z.string(),
    body: z.string(), // markdown
    options: z.any().nullable(),
    proposedAction: z.any().nullable(),
    status: z.enum(['pending', 'approved', 'rejected', 'answered', 'expired']),
    resolution: z.any().nullable(),
    resolvedBy: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    dueBy: z.string().nullable(),
    createdAt: z.string(),
  });
  export type AgentDecision = z.infer<typeof AgentDecisionSchema>;

  // Health roll-up is computed server-side, deterministically (no LLM).
  export const OpsHealthSchema = z.enum(['green', 'yellow', 'red']);
  export type OpsHealth = z.infer<typeof OpsHealthSchema>;

  export const OpsRunSummarySchema = z.object({
    id: z.string(),
    taskType: z.string(),
    status: AgentRunStatusSchema,
    finishedAt: z.string().nullable(),
    costUsd: z.number().nullable(),
  });
  export type OpsRunSummary = z.infer<typeof OpsRunSummarySchema>;

  export const OpsMilestoneSchema = z.object({
    task: z.string(), // AgentTaskType or 'lock_notify' (the existing T-24h handler, read-only)
    label: z.string(),
    plannedAt: z.string(), // ISO, in event's city timezone context
    status: z.enum(['pending', 'scheduled', 'running', 'done', 'blocked', 'skipped']),
    runId: z.string().nullable(),
  });
  export type OpsMilestone = z.infer<typeof OpsMilestoneSchema>;

  export const OpsNeighborhoodStatsSchema = z.object({
    neighborhoodId: z.string(),
    neighborhoodName: z.string(),
    health: OpsHealthSchema,
    healthReason: z.string().nullable(),
    booked: z.number(),
    seated: z.number(),
    unassigned: z.number(),
    tables: z.number(),
    tableSizes: z.array(z.number()), // e.g. [6, 6, 5]
    lockedTables: z.number(),
    reserved: z.number(),
    reservationsNeeded: z.number(),
    pendingDecisions: z.number(),
    lastRun: OpsRunSummarySchema.nullable(),
    nextMilestone: OpsMilestoneSchema.nullable(),
    // Full pipeline for this scope, in execution order — the dashboard's
    // per-neighborhood stage indicators render from this.
    milestones: z.array(OpsMilestoneSchema),
    automationEnabled: z.boolean(),
  });
  export type OpsNeighborhoodStats = z.infer<typeof OpsNeighborhoodStatsSchema>;

  // One tile per (event × city) — hover-card data included so hovering
  // costs no extra request.
  export const OpsTileSchema = z.object({
    eventTimeId: z.string(),
    date: z.string(),
    time: z.string(),
    eventType: z.string(), // dinner | brunch | coffee — coffee takes no reservations
    cityId: z.string(),
    cityName: z.string(),
    timezone: z.string(),
    localDinnerAt: z.string(), // ISO with offset, dinner time in city tz
    health: OpsHealthSchema,
    healthReason: z.string().nullable(), // single worst condition when not green
    phase: z.string(), // furthest milestone reached, e.g. "matched → reservations"
    booked: z.number(),
    seated: z.number(),
    unassigned: z.number(),
    tables: z.number(),
    reserved: z.number(),
    reservationsNeeded: z.number(),
    newSinceLastRun: z.number(),
    cancelledSinceLastRun: z.number(),
    pendingDecisions: z.number(),
    nearestDecisionDueBy: z.string().nullable(),
    // Event-level stages (event-scoped tasks, e.g. the reservation audit) —
    // same array on every city tile of the event; runId of the completed run
    // opens the audit snapshot report.
    eventStages: z.array(OpsMilestoneSchema),
    lastRun: OpsRunSummarySchema.nullable(),
    nextMilestone: OpsMilestoneSchema.nullable(),
    neighborhoods: z.array(OpsNeighborhoodStatsSchema),
  });
  export type OpsTile = z.infer<typeof OpsTileSchema>;

  // GET /api/admin/ops/overview
  export type GetOpsOverviewResponse = ApiResponse<{
    tiles: OpsTile[];
    pendingDecisionsTotal: number;
  }>;

  // Event context for a single (event × neighborhood) scope.
  export const OpsScopeEventSchema = z.object({
    eventTimeId: z.string(),
    date: z.string(),
    time: z.string(),
    eventType: z.string(), // dinner | brunch | coffee — coffee takes no reservations
    cityId: z.string(),
    cityName: z.string(),
    timezone: z.string(),
    localDinnerAt: z.string(),
  });
  export type OpsScopeEvent = z.infer<typeof OpsScopeEventSchema>;

  // GET /api/admin/ops/events/:eventTimeId/:neighborhoodId — the detail view
  // is strictly per neighborhood; cities are never combined here.
  export type GetOpsScopeDetailResponse = ApiResponse<{
    event: OpsScopeEvent;
    neighborhood: OpsNeighborhoodStats;
    milestones: OpsMilestone[];
    runs: AgentRun[];
    decisions: AgentDecision[];
  }>;

  // GET /api/admin/ops/runs — cursor-paginated run list
  export const GetAgentRunsRequestSchema = z.object({
    eventTimeId: z.string().optional(),
    neighborhoodId: z.string().optional(),
    status: AgentRunStatusSchema.optional(),
    limit: z.number().int().min(1).max(100).optional(),
    before: z.string().optional(),
  });
  export type GetAgentRunsRequest = z.infer<typeof GetAgentRunsRequestSchema>;
  export type GetAgentRunsResponse = ApiResponse<{
    runs: AgentRun[];
    nextBefore: string | null;
  }>;

  // GET /api/admin/ops/runs/:id — full run detail: narrative events +
  // audited actions (joined from events_audit_logs via agent_run_id).
  export type GetAgentRunDetailResponse = ApiResponse<{
    run: AgentRun;
    events: AgentRunEvent[];
    auditEntries: AuditLogEntry[];
    decisions: AgentDecision[];
  }>;

  // GET /api/admin/ops/decisions
  export const GetAgentDecisionsRequestSchema = z.object({
    status: z.enum(['pending', 'approved', 'rejected', 'answered', 'expired']).optional(),
    eventTimeId: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    before: z.string().optional(),
  });
  export type GetAgentDecisionsRequest = z.infer<typeof GetAgentDecisionsRequestSchema>;
  // A gated action is stored as ids because that is what the server executes.
  // The inbox needs the people and tables those ids stand for, so the API
  // resolves them alongside each decision rather than making the card guess.
  export interface DecisionGuest {
    bookingId: string;
    userId: string;
    name: string;
    /** The guest this decision is about, for emphasis in the composition. */
    isSubject: boolean;
    specialRequests: string | null;
    notified: boolean;
    flaggedForReview: boolean;
  }

  export interface DecisionTable {
    id: string;
    reservationName: string | null;
    restaurantName: string | null;
    neighborhoodId: string | null;
    capacity: number;
    locked: boolean;
    reservationStatus: string | null;
    guests: DecisionGuest[];
  }

  export interface DecisionContext {
    kind: 'move' | 'cant_be_seated' | 'create_restaurant' | 'batch' | 'other';
    subject: DecisionGuest | null;
    /** Where the subject sits now; null when unassigned. */
    fromTable: DecisionTable | null;
    /** Where they would end up; null for can't-be-seated. */
    toTable: DecisionTable | null;
    proposedRestaurant: { name?: string; address?: string; neighborhoodId?: string } | null;
    /** For batch (several guests, one approval): one context per guest. */
    items?: DecisionContext[];
  }

  export type GetAgentDecisionsResponse = ApiResponse<{
    decisions: AgentDecision[];
    /** Keyed by decision id; only present for decisions carrying a proposedAction. */
    contexts: Record<string, DecisionContext>;
    nextBefore: string | null;
    pendingCount: number;
  }>;

  // POST /api/admin/ops/decisions/:id/resolve
  export const ResolveAgentDecisionRequestSchema = z.object({
    resolution: z.enum(['approved', 'rejected', 'answered']),
    answer: z.string().optional(), // for question-kind decisions
    note: z.string().optional(),
  });
  export type ResolveAgentDecisionRequest = z.infer<typeof ResolveAgentDecisionRequestSchema>;
  export type ResolveAgentDecisionResponse = ApiResponse<{
    decision: AgentDecision;
    followUpRunId: string | null;
  }>;

  // POST /api/admin/ops/runs — manual "Run now"
  export const EnqueueAgentRunRequestSchema = z.object({
    taskType: AgentTaskTypeSchema,
    eventTimeId: z.string(),
    neighborhoodId: z.string(),
    note: z.string().optional(), // included in the run's instructions/input
  });
  export type EnqueueAgentRunRequest = z.infer<typeof EnqueueAgentRunRequestSchema>;
  export type EnqueueAgentRunResponse = ApiResponse<{
    runId: string;
    // 'queued' = new run; 'existing' = active run already present for the
    // scope, rerun_requested was set instead.
    outcome: 'queued' | 'existing';
  }>;

  // GET /api/admin/ops/activity — cross-run action feed: recent runs with
  // their audited actions and raised decisions, grouped per run. Neighborhood
  // filter is server-side; action-type filtering happens client-side.
  export const GetOpsActivityRequestSchema = z.object({
    neighborhoodId: z.string().optional(),
    eventTimeId: z.string().optional(),
    limit: z.number().min(1).max(100).optional(),
    before: z.string().optional(), // run createdAt cursor
  });
  export type GetOpsActivityRequest = z.infer<typeof GetOpsActivityRequestSchema>;
  export interface OpsActivityDecision {
    id: string;
    title: string;
    kind: 'approval' | 'question' | 'escalation';
    status: string;
    createdAt: string;
  }
  export interface OpsActivityRun {
    run: AgentRun;
    actions: AuditLogEntry[];
    decisions: OpsActivityDecision[];
  }
  export type GetOpsActivityResponse = ApiResponse<{
    runs: OpsActivityRun[];
    nextBefore: string | null;
  }>;

  // POST /api/admin/ops/runs/:id/cancel
  export type CancelAgentRunResponse = ApiResponse<{ cancelled: boolean }>;

  // POST /api/admin/ops/sweep — manual change sweep for ONE event (the
  // watcher's hourly webhook sweeps all events): enqueue re-optimizes for
  // scopes with changed seating.
  export const OpsSweepRequestSchema = z.object({
    eventTimeId: z.string(),
  });
  export type OpsSweepRequest = z.infer<typeof OpsSweepRequestSchema>;
  export type OpsSweepResponse = ApiResponse<{
    scopesChecked: number;
    changed: number;
    enqueued: number;
    skippedUnchanged: number;
    retriedUnhealthy: number;
    skippedExhausted: number;
    details: string[];
  }>;

  // The conductor conversation (ops console). A turn is an agent run: posting
  // a message enqueues `conductor.reply`, and that run's report is written
  // back as the assistant turn, so answers arrive by polling rather than in
  // the POST response.
  export interface ConductorTurn {
    id: string;
    role: "user" | "assistant" | "system";
    content: string;
    runId: string | null;
    /** Status of the run behind this turn: how the UI knows it is thinking. */
    runStatus: "queued" | "running" | "completed" | "failed" | "cancelled" | null;
    createdAt: string;
  }

  // GET /api/admin/ops/conductor/thread?eventTimeId=…
  export type ConductorThreadResponse = ApiResponse<{
    turns: ConductorTurn[];
    /** True while a turn is queued or running — the UI shows a thinking state. */
    pending: boolean;
  }>;

  // POST /api/admin/ops/conductor/messages
  export const ConductorMessageRequestSchema = z.object({
    eventTimeId: z.string(),
    content: z.string().min(1).max(4000),
  });
  export type ConductorMessageRequest = z.infer<typeof ConductorMessageRequestSchema>;
  export type ConductorMessageResponse = ApiResponse<{
    messageId: string;
    runId: string | null;
    blockedReason?: string;
  }>;

  // POST /api/admin/ops/runs/:id/resume — send a follow-up instruction to a
  // finished run: enqueues a new run for the same scope that resumes the
  // original Claude session (when its session id was recorded) with the
  // instruction, full prior context intact.
  export const ResumeAgentRunRequestSchema = z.object({
    instruction: z.string().min(1),
  });
  export type ResumeAgentRunRequest = z.infer<typeof ResumeAgentRunRequestSchema>;
  export type ResumeAgentRunResponse = ApiResponse<{
    outcome: string;
    runId: string | null;
  }>;

  // POST /api/admin/ops/events/:eventTimeId/runs — run one pipeline stage for
  // every neighborhood participating in the event (across all its cities).
  export const BulkEnqueueAgentRunsRequestSchema = z.object({
    taskType: AgentTaskTypeSchema,
    // Default true: skip neighborhoods where this stage already completed, or
    // is currently queued/running for them. false = force it for everyone.
    onlyIfNotYetRun: z.boolean().optional().default(true),
    note: z.string().optional(),
  });
  export type BulkEnqueueAgentRunsRequest = z.infer<typeof BulkEnqueueAgentRunsRequestSchema>;

  export const BulkEnqueueOutcomeSchema = z.enum([
    'queued',
    'existing',
    'duplicate',
    'disabled',
    'capped',
    'skipped_already_ran',
    'skipped_in_progress',
  ]);
  export type BulkEnqueueOutcome = z.infer<typeof BulkEnqueueOutcomeSchema>;

  export const BulkEnqueueResultSchema = z.object({
    neighborhoodId: z.string(),
    neighborhoodName: z.string(),
    outcome: BulkEnqueueOutcomeSchema,
    runId: z.string().nullable(),
  });
  export type BulkEnqueueResult = z.infer<typeof BulkEnqueueResultSchema>;

  export type BulkEnqueueAgentRunsResponse = ApiResponse<{
    results: BulkEnqueueResult[];
  }>;

  // ==========================================================================
  // Feedback Triage (inbound email hub — docs/TRIAGE.md). Ticket = the triage
  // unit; threads are channel containers; drafts are the reply-review queue.
  // ==========================================================================

  export const TriageTicketStatusSchema = z.enum([
    'new',
    'classified',
    'awaiting_approval',
    'waiting_on_user',
    'resolved',
    'spam',
    'merged',
  ]);
  export type TriageTicketStatus = z.infer<typeof TriageTicketStatusSchema>;

  export const TriageDispositionSchema = z.enum([
    'auto_answerable',
    'needs_review',
    'needs_action',
    'spam',
  ]);
  export type TriageDisposition = z.infer<typeof TriageDispositionSchema>;

  export interface TriageMatchCandidate {
    userId: string;
    firstName: string;
    lastName: string | null;
    username: string;
    email: string | null;
    city: string | null;
    profileImageUrl: string | null;
    method: 'exact' | 'alias' | 'fuzzy';
    score: number; // 0-100
  }

  export interface TriageTicketSummary {
    id: string;
    status: TriageTicketStatus;
    disposition: TriageDisposition | null;
    category: string | null;
    priority: string;
    title: string | null;
    summary: string | null;
    sentiment: number | null;
    counterpartEmail: string | null;
    counterpartName: string | null;
    matchedUserId: string | null;
    matchedUserName: string | null;
    matchedUserImageUrl: string | null;
    matchConfidence: string | null;
    threadCount: number;
    hasActiveDraft: boolean;
    lastInboundAt: string | null;
    lastOutboundAt: string | null;
    createdAt: string;
  }

  export interface TriageThreadInfo {
    id: string;
    source: string;
    externalThreadId: string;
    subject: string | null;
    counterpartEmail: string | null;
    counterpartName: string | null;
    attachMethod: string;
    lastMessageAt: string | null;
  }

  export interface TriageMessageInfo {
    id: string;
    threadId: string;
    direction: 'inbound' | 'outbound';
    fromEmail: string | null;
    fromName: string | null;
    toEmails: string[] | null;
    snippet: string | null;
    bodyText: string | null;
    sentAt: string;
  }

  // Side effects a resolution option can carry (docs/TRIAGE.md §2a). The agent
  // proposes them alongside a reply; nothing runs until an admin presses
  // "Do it & send" on that option. All act on the ticket's matched user.
  export const TriageActionSchema = z.discriminatedUnion('type', [
    // One-time dinner ticket(s), as the admin "grant entitlement" button does.
    z.object({ type: z.literal('grant_ticket'), count: z.number().int().min(1).max(3) }),
    // Push the next Stripe charge out by N days (native subs cannot be extended).
    z.object({ type: z.literal('extend_subscription'), days: z.number().int().min(1).max(90) }),
    // Stripe only: 'period_end' stops renewal, 'now' ends access immediately.
    z.object({ type: z.literal('cancel_subscription'), when: z.enum(['now', 'period_end']) }),
    // Stripe only. paymentId defaults to the latest refundable payment; amount
    // (major units) defaults to everything still refundable on it.
    z.object({
      type: z.literal('refund_payment'),
      paymentId: z.string().optional(),
      amount: z.number().positive().optional(),
    }),
    // Cancel a booking; restoreTicket re-activates the one-time ticket it used.
    z.object({
      type: z.literal('cancel_booking'),
      bookingId: z.string(),
      restoreTicket: z.boolean(),
    }),
  ]);
  export type TriageAction = z.infer<typeof TriageActionSchema>;
  export type TriageActionType = TriageAction['type'];

  export interface TriageActionResult {
    action: TriageAction;
    ok: boolean;
    message: string;
    at: string;
  }

  // What the actions can act on, for labelling them ("Refund $29 from Sep 3")
  // and for the agent's work order. Null fields mean the action is unavailable.
  export interface TriageAccountContext {
    userId: string;
    stripeSubscription: {
      entitlementId: string;
      status: string;
      renewsAt: string | null;
      // Set once the subscription is scheduled to end (cancelled at period end).
      endsAt: string | null;
    } | null;
    // App Store / Play Store subscription: we cannot cancel or refund it.
    nativeSubscription: { status: string; managementUrl: string | null } | null;
    // Newest first; only Stripe payments are refundable from here.
    recentPayments: Array<{
      id: string;
      amount: number;
      refundedAmount: number;
      currency: string;
      type: string;
      processor: string;
      status: string;
      createdAt: string;
      refundable: boolean;
    }>;
    activeTickets: number;
    upcomingBookings: Array<{
      bookingId: string;
      date: string;
      time: string;
      neighborhoodName: string | null;
      usesTicket: boolean;
    }>;
  }

  export interface TriageDraftInfo {
    id: string;
    ticketId: string;
    targetThreadId: string;
    // Short name of this resolution option ("Refund and cancel"); null on
    // plain reply drafts.
    label: string | null;
    body: string;
    originalBody: string;
    rationale: string | null;
    confidence: number | null;
    actions: TriageAction[];
    actionResults: TriageActionResult[];
    status: 'proposed' | 'edited' | 'approved_sent' | 'rejected' | 'superseded';
    reviewNote: string | null;
    reviewedAt: string | null;
    createdAt: string;
  }

  export interface TriageHistoryItem {
    id: string;
    title: string | null;
    category: string | null;
    status: TriageTicketStatus;
    summary: string | null;
    lastInboundAt: string | null;
    createdAt: string;
  }

  export interface TriageSuggestedMerge {
    id: string;
    title: string | null;
    status: TriageTicketStatus;
    counterpartEmail: string | null;
    lastInboundAt: string | null;
  }

  export interface TriageMatchedUserCard {
    userId: string;
    firstName: string;
    lastName: string | null;
    username: string;
    email: string | null;
    city: string | null;
    profileImageUrl: string | null;
    upcomingBooking: { eventTimeId: string; date: string; time: string; neighborhoodName: string | null } | null;
    lastAttended: { date: string; neighborhoodName: string | null } | null;
    subscriptionStatus: string | null;
    // Newest non-deleted Stripe customer for the user, for a dashboard deep link.
    stripeCustomerId: string | null;
  }

  // GET /api/admin/triage/tickets
  export const GetTriageTicketsRequestSchema = z.object({
    status: z.string().optional(), // 'open' (default) or an exact status
    disposition: TriageDispositionSchema.optional(),
    category: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    before: z.string().optional(),
  });
  export type GetTriageTicketsRequest = z.infer<typeof GetTriageTicketsRequestSchema>;
  export type GetTriageTicketsResponse = ApiResponse<{
    tickets: TriageTicketSummary[];
    nextBefore: string | null;
  }>;

  // GET /api/admin/triage/counts — nav badge + inbox filter counts
  export type GetTriageCountsResponse = ApiResponse<{
    open: number;
    needsReview: number;
    needsAction: number;
    autoAnswerable: number;
    unclassified: number;
    awaitingApproval: number;
    waitingOnUser: number;
    pendingPlaybookProposals: number;
  }>;

  // GET /api/admin/triage/tickets/:id
  export type GetTriageTicketDetailResponse = ApiResponse<{
    ticket: TriageTicketSummary;
    threads: TriageThreadInfo[];
    messages: TriageMessageInfo[];
    drafts: TriageDraftInfo[];
    matchCandidates: TriageMatchCandidate[];
    matchedUser: TriageMatchedUserCard | null;
    history: TriageHistoryItem[];
    suggestedMerges: TriageSuggestedMerge[];
    similar: Array<{ id: string; title: string | null; category: string | null; status: string }>;
    accountContext: TriageAccountContext | null;
  }>;

  // POST /api/admin/triage/tickets/:id/status
  export const SetTriageStatusRequestSchema = z.object({
    status: TriageTicketStatusSchema.exclude(['merged']),
  });
  export type SetTriageStatusRequest = z.infer<typeof SetTriageStatusRequestSchema>;

  // POST /api/admin/triage/tickets/:id/link-user
  export const LinkTriageUserRequestSchema = z.object({
    userId: z.string().nullable(),
  });
  export type LinkTriageUserRequest = z.infer<typeof LinkTriageUserRequestSchema>;

  // POST /api/admin/triage/tickets/:id/classify — admin override of the
  // classification fields.
  export const UpdateTriageTicketRequestSchema = z.object({
    disposition: TriageDispositionSchema.optional(),
    category: z.string().optional(),
    priority: z.enum(['low', 'normal', 'high']).optional(),
    title: z.string().optional(),
  });
  export type UpdateTriageTicketRequest = z.infer<typeof UpdateTriageTicketRequestSchema>;

  // POST /api/admin/triage/tickets/:id/merge
  export const MergeTriageTicketsRequestSchema = z.object({
    sourceTicketIds: z.array(z.string()).min(1),
  });
  export type MergeTriageTicketsRequest = z.infer<typeof MergeTriageTicketsRequestSchema>;
  export type MergeTriageTicketsResponse = ApiResponse<{ mergedCount: number }>;

  // POST /api/admin/triage/threads/:id/unmerge
  export type UnmergeTriageThreadResponse = ApiResponse<{ newTicketId: string }>;

  // PUT /api/admin/triage/drafts/:id
  export const EditTriageDraftRequestSchema = z.object({
    body: z.string().min(1),
    reviewNote: z.string().optional(),
  });
  export type EditTriageDraftRequest = z.infer<typeof EditTriageDraftRequestSchema>;

  // POST /api/admin/triage/drafts/:id/send
  export const SendTriageDraftRequestSchema = z.object({
    // Optionally edit-and-send in one call.
    body: z.string().min(1).optional(),
    resolve: z.boolean().optional(), // true → resolved, else waiting_on_user
    // The final action list (the admin may drop or add actions); omitted =
    // the draft's own. Actions run BEFORE the email; any failure stops the send.
    actions: z.array(TriageActionSchema).max(6).optional(),
  });
  export type SendTriageDraftRequest = z.infer<typeof SendTriageDraftRequestSchema>;
  export type SendTriageDraftResponse = ApiResponse<{ ok: boolean; actionResults: TriageActionResult[] }>;

  // POST /api/admin/triage/tickets/:id/reply — a reply the admin wrote
  // themselves (no suggestion needed), with optional actions. Created as a
  // draft and sent through the same pipeline as a suggested option.
  export const SendTriageReplyRequestSchema = z.object({
    body: z.string().trim().min(1),
    resolve: z.boolean().optional(),
    actions: z.array(TriageActionSchema).max(6).optional(),
  });
  export type SendTriageReplyRequest = z.infer<typeof SendTriageReplyRequestSchema>;

  // POST /api/admin/triage/tickets/:id/assist — "ask AI": turn the admin's
  // instruction ("reply in French", "apologise and add a free ticket") into
  // one more resolution option. currentBody/currentActions carry whatever the
  // admin has open, so "translate this" works on unsaved edits.
  export const TriageAssistRequestSchema = z.object({
    instruction: z.string().trim().min(3).max(2000),
    currentBody: z.string().max(20000).optional(),
    currentActions: z.array(TriageActionSchema).max(6).optional(),
  });
  export type TriageAssistRequest = z.infer<typeof TriageAssistRequestSchema>;
  export type TriageAssistResponse = ApiResponse<{ draftId: string; droppedActions: string[] }>;

  // POST /api/admin/triage/drafts/:id/reject
  export const RejectTriageDraftRequestSchema = z.object({
    note: z.string().optional(), // "too formal" — feeds the reflect loop
  });
  export type RejectTriageDraftRequest = z.infer<typeof RejectTriageDraftRequestSchema>;

  // GET /api/admin/triage/user-search?q=
  export type TriageUserSearchResponse = ApiResponse<{ candidates: TriageMatchCandidate[] }>;

  // GET /api/admin/triage/history?userId=…|email=…
  export type GetTriageHistoryResponse = ApiResponse<{ tickets: TriageHistoryItem[] }>;

  // POST /api/admin/triage/tickets/:id/remember — "Remember this" teach box;
  // files a pending playbook proposal for the rules doc.
  export const TriageRememberRequestSchema = z.object({
    text: z.string().min(3).max(500),
  });
  export type TriageRememberRequest = z.infer<typeof TriageRememberRequestSchema>;

  // Playbook
  export interface TriagePlaybookDocInfo {
    slug: string;
    title: string;
    body: string;
    updatedAt: string;
  }
  export interface TriagePlaybookRevisionInfo {
    id: string;
    slug: string;
    body: string;
    rationale: string | null;
    source: string; // manual | proposal | remember | seed
    status: 'applied' | 'pending' | 'rejected';
    reviewNote: string | null;
    reviewedAt: string | null;
    createdAt: string;
  }
  export type GetTriagePlaybookResponse = ApiResponse<{
    docs: TriagePlaybookDocInfo[];
    pendingProposals: TriagePlaybookRevisionInfo[];
  }>;
  export const UpdateTriagePlaybookDocRequestSchema = z.object({
    body: z.string().min(1),
    title: z.string().optional(),
  });
  export type UpdateTriagePlaybookDocRequest = z.infer<typeof UpdateTriagePlaybookDocRequestSchema>;
  export type GetTriagePlaybookRevisionsResponse = ApiResponse<{
    revisions: TriagePlaybookRevisionInfo[];
  }>;
  export const ReviewTriageProposalRequestSchema = z.object({
    action: z.enum(['approve', 'reject']),
    editedBody: z.string().optional(), // approve with the reviewer's edits
    note: z.string().optional(), // for reject
  });
  export type ReviewTriageProposalRequest = z.infer<typeof ReviewTriageProposalRequestSchema>;

  // POST /api/admin/triage/tickets/resolve-all — "Mark all done": resolve every
  // ticket matching the inbox view in one sweep and supersede their pending
  // drafts. With no body it resolves the open set (new/classified/
  // awaiting_approval); `status` narrows to one open status (or the
  // waiting_on_user set, which the default sweep leaves alone) and
  // `disposition` narrows further. Closed statuses are never accepted.
  export const ResolveAllTriageTicketsRequestSchema = z.object({
    status: z.enum(['open', 'new', 'classified', 'awaiting_approval', 'waiting_on_user']).optional(),
    disposition: TriageDispositionSchema.optional(),
  });
  export type ResolveAllTriageTicketsRequest = z.infer<typeof ResolveAllTriageTicketsRequestSchema>;
  export type ResolveAllTriageTicketsResponse = ApiResponse<{ resolved: number }>;

  // POST /api/admin/triage/runs — enqueue a global (ticket-less) triage run.
  // `triage.reflect` is the learning loop (docs/TRIAGE.md §5): it reviews
  // draft edits, rejections and outcomes and proposes playbook revisions.
  export const EnqueueTriageGlobalRunRequestSchema = z.object({
    taskType: z.enum(['triage.reflect', 'triage.bootstrap']),
    note: z.string().optional(),
  });
  export type EnqueueTriageGlobalRunRequest = z.infer<typeof EnqueueTriageGlobalRunRequestSchema>;
  export type EnqueueTriageGlobalRunResponse = ApiResponse<{
    runId: string;
    outcome: 'queued' | 'existing';
  }>;

  // POST /api/admin/triage/sync — manual Gmail sync trigger
  export type TriageSyncResponse = ApiResponse<{
    enabled: boolean;
    inbound?: number;
    outbound?: number;
    ignored?: number;
    ticketsCreated?: number;
    skipped?: string;
    error?: string;
  }>;

  // --------------------------------------------------------------------------
  // No-show fees (/api/admin/ops/no-shows)
  // --------------------------------------------------------------------------

  // Layered: each mode does everything the one before it does.
  export type NoShowFeeMode = 'off' | 'track' | 'warn' | 'charge';

  export const NoShowCaseStatusSchema = z.enum(['detected', 'notified', 'excused', 'charged', 'charge_failed']);
  export type NoShowCaseStatus = z.infer<typeof NoShowCaseStatusSchema>;

  export interface NoShowCaseRow {
    id: string;
    bookingId: string;
    userId: string;
    userName: string;
    userEmail: string | null;
    eventTimeId: string;
    eventDate: string;
    restaurantName: string | null;
    status: NoShowCaseStatus;
    reportCount: number;
    disclaimerAccepted: boolean;
    // What this guest is/was charged, in their currency (smallest unit).
    feeAmount: number;
    currency: string;
    notifiedAt: string | null;
    chargeAfter: string | null;
    excusedAt: string | null;
    excusedByName: string | null;
    excuseReason: string | null;
    chargedAt: string | null;
    stripePaymentIntentId: string | null;
    chargeError: string | null;
    createdAt: string;
  }

  // GET /api/admin/ops/no-shows query. Dates are the DINNER date (YYYY-MM-DD).
  export const GetNoShowsRequestSchema = z.object({
    status: NoShowCaseStatusSchema.optional(),
    disclaimer: z.enum(['with', 'without']).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    q: z.string().max(100).optional(), // name or email
  });
  export type GetNoShowsRequest = z.infer<typeof GetNoShowsRequestSchema>;

  // GET /api/admin/ops/no-shows. Config is server-wide and hard-coded
  // (services/noShow/config.ts); read-only here.
  export type GetNoShowsResponse = ApiResponse<{
    config: { mode: NoShowFeeMode; graceHours: number; minReports: number; fees: Record<string, number> };
    cases: NoShowCaseRow[];
    // Per-status totals, unfiltered, for the filter chips.
    counts: Partial<Record<NoShowCaseStatus, number>>;
  }>;

  // POST /api/admin/ops/no-shows/cases — open a case by hand
  export const OpenNoShowCaseRequestSchema = z.object({
    bookingId: z.string().min(1),
  });
  export type NoShowCaseActionResponse = ApiResponse<{ caseId: string; status: NoShowCaseStatus }>;

  // POST /api/admin/ops/no-shows/cases/batch — one action on many cases.
  // Notices go out as a single Customer.io broadcast; charges are one Stripe
  // call each (there is no batch PaymentIntent) followed by one receipt broadcast.
  export const NoShowBatchActionSchema = z.enum(['warn', 'fee_notice', 'charge', 'excuse', 'reopen']);
  export type NoShowBatchAction = z.infer<typeof NoShowBatchActionSchema>;
  export const NoShowBatchActionRequestSchema = z.object({
    action: NoShowBatchActionSchema,
    caseIds: z.array(z.string().min(1)).min(1).max(200),
    reason: z.string().max(1000).optional(), // excuse only
  });
  export type NoShowBatchActionRequest = z.infer<typeof NoShowBatchActionRequestSchema>;
  export type NoShowBatchActionResponse = ApiResponse<{
    ok: string[];
    failed: Array<{ caseId: string; error: string }>;
  }>;

  // POST /api/admin/ops/no-shows/run — run the pass now (dry run by default)
  export const RunNoShowReconcilerRequestSchema = z.object({
    dryRun: z.boolean().optional(),
  });
  export type RunNoShowReconcilerResponse = ApiResponse<{
    dryRun: boolean;
    detected: number;
    notified: number;
    charged: number;
    failed: number;
    details: string[];
  }>;
}

// ========== Poll Types (shared between Admin and User) ==========
// Optional presentation fields for a poll option. When any option sets them
// the poll renders as rich cards (image, subtitle, tags, blurb, link) — e.g.
// candidate books for a book club. Plain polls just use `text`.
export const PollAnswerDetailsSchema = z.object({
  imageUrl: z.string().url().nullable().optional(),
  subtitle: z.string().max(200).nullable().optional(), // e.g. "Author · 392 pp"
  tags: z.array(z.string().max(40)).max(5).optional(),
  description: z.string().max(500).nullable().optional(), // short pitch
  linkUrl: z.string().url().nullable().optional(), // "More about it"
});

export const PollAnswerSchema = PollAnswerDetailsSchema.extend({
  id: z.string(),
  text: z.string(),
});

export const PollTargetCriteriaSchema = z.object({
  subscribersOnly: z.boolean().optional(),
  notSubscribed: z.boolean().optional(),
  hasSpentMoney: z.boolean().optional(),
  cities: z.array(z.string()).optional(),
  quizAnswerRegex: z.array(z.object({
    field: z.string(),
    pattern: z.string(),
  })).optional(),
});

export type PollAnswer = z.infer<typeof PollAnswerSchema>;
export type PollAnswerDetails = z.infer<typeof PollAnswerDetailsSchema>;
export type PollTargetCriteria = z.infer<typeof PollTargetCriteriaSchema>;

// ========== Poll Endpoints (Admin) ==========
export namespace PollAPI {
  // Shared poll schema used by both admin and user
  export const PollSchema = z.object({
    id: z.string(),
    title: z.string(),
    question: z.string(),
    answers: z.array(PollAnswerSchema),
    status: z.enum(['draft', 'active', 'closed', 'archived']),
    publicResults: z.boolean(),
    targetCriteria: PollTargetCriteriaSchema.nullable(),
    startsAt: z.date().nullable(),
    endsAt: z.date().nullable(),
    createdBy: z.string().nullable(),
    clubId: z.string().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
  });

  export type Poll = z.infer<typeof PollSchema>;

  // Poll results schema
  export const PollResultSchema = z.object({
    answerId: z.string(),
    answerText: z.string(),
    count: z.number(),
    percentage: z.number(),
  });

  // Per-city poll results
  export const CityPollResultSchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    results: z.array(PollResultSchema),
    totalResponses: z.number(),
  });

  export const PollWithResultsSchema = PollSchema.extend({
    results: z.array(PollResultSchema),
    totalResponses: z.number(),
    resultsByCity: z.array(CityPollResultSchema).optional(),
    userResponse: z.string().nullable().optional(), // The user's answer if they voted
  });

  export type PollResult = z.infer<typeof PollResultSchema>;
  export type CityPollResult = z.infer<typeof CityPollResultSchema>;
  export type PollWithResults = z.infer<typeof PollWithResultsSchema>;

  // Admin: Create poll request
  export const CreatePollRequestSchema = z.object({
    title: z.string().min(1, "Title is required"),
    question: z.string().min(1, "Question is required"),
    answers: z.array(PollAnswerDetailsSchema.extend({
      text: z.string().min(1, "Answer text is required"),
    })).min(2, "At least 2 answers are required"),
    publicResults: z.boolean().optional().default(false),
    targetCriteria: PollTargetCriteriaSchema.nullable().optional(),
    startsAt: z.string().nullable().optional(), // ISO date string
    endsAt: z.string().nullable().optional(), // ISO date string
    clubId: z.string().nullable().optional(), // Scope the poll to a club
  });

  export type CreatePollRequest = z.infer<typeof CreatePollRequestSchema>;
  export type CreatePollResponse = ApiResponse<Poll>;

  // Admin: Update poll request
  export const UpdatePollRequestSchema = z.object({
    title: z.string().min(1).optional(),
    question: z.string().min(1).optional(),
    answers: z.array(PollAnswerDetailsSchema.extend({
      id: z.string().optional(), // Existing answers have IDs
      text: z.string().min(1),
    })).optional(),
    status: z.enum(['draft', 'active', 'closed', 'archived']).optional(),
    publicResults: z.boolean().optional(),
    targetCriteria: PollTargetCriteriaSchema.nullable().optional(),
    startsAt: z.string().nullable().optional(),
    endsAt: z.string().nullable().optional(),
    clubId: z.string().nullable().optional(),
  });

  export type UpdatePollRequest = z.infer<typeof UpdatePollRequestSchema>;
  export type UpdatePollResponse = ApiResponse<Poll>;

  // Admin: Get all polls
  export type GetPollsResponse = ApiResponse<PollWithResults[]>;

  // Admin: Get single poll with results
  export type GetPollResponse = ApiResponse<PollWithResults>;

  // Admin: Delete poll
  export type DeletePollResponse = ApiResponse<void>;

  // Admin: Preview target count
  export const PreviewTargetRequestSchema = PollTargetCriteriaSchema;

  export const PreviewTargetDataSchema = z.object({
    count: z.number(),
  });

  export type PreviewTargetRequest = z.infer<typeof PreviewTargetRequestSchema>;
  export type PreviewTargetData = z.infer<typeof PreviewTargetDataSchema>;
  export type PreviewTargetResponse = ApiResponse<PreviewTargetData>;

  // User: Get available polls (polls they can see and haven't answered or can see results)
  export const UserPollSchema = z.object({
    id: z.string(),
    title: z.string(),
    question: z.string(),
    answers: z.array(PollAnswerSchema),
    publicResults: z.boolean(),
    hasResponded: z.boolean(),
    userResponse: z.string().nullable(), // The answer ID if responded
    results: z.array(PollResultSchema).nullable(), // Only if publicResults=true or hasResponded
    totalResponses: z.number().nullable(),
    endsAt: z.string().nullable().optional(), // ISO; set for club polls (countdown chip)
  });

  export type UserPoll = z.infer<typeof UserPollSchema>;
  export type GetUserPollsResponse = ApiResponse<UserPoll[]>;
  export type GetUserPollResponse = ApiResponse<UserPoll>;

  // User: Submit poll response
  export const SubmitPollResponseRequestSchema = z.object({
    answerId: z.string(),
  });

  export const SubmitPollResponseDataSchema = z.object({
    results: z.array(PollResultSchema).nullable(), // Returns results if publicResults=true
    totalResponses: z.number(),
  });

  export type SubmitPollResponseRequest = z.infer<typeof SubmitPollResponseRequestSchema>;
  export type SubmitPollResponseData = z.infer<typeof SubmitPollResponseDataSchema>;
  export type SubmitPollResponseResponse = ApiResponse<SubmitPollResponseData>;
}

// ========== Admin Bookings Summary ==========
export namespace BookingsSummaryAPI {
  export const EventBookingsCitySchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    count: z.number(),
  });

  export const EventBookingsSummarySchema = z.object({
    eventTimeId: z.string(),
    date: z.string(),
    time: z.string(),
    isPast: z.boolean(),
    totalBookings: z.number(),
    bookingsByCity: z.array(EventBookingsCitySchema),
  });

  export const BookingsSummaryDataSchema = z.object({
    events: z.array(EventBookingsSummarySchema),
    cities: z.array(z.object({
      id: z.string(),
      name: z.string(),
    })),
  });

  export type EventBookingsCity = z.infer<typeof EventBookingsCitySchema>;
  export type EventBookingsSummary = z.infer<typeof EventBookingsSummarySchema>;
  export type BookingsSummaryData = z.infer<typeof BookingsSummaryDataSchema>;
  export type GetBookingsSummaryResponse = ApiResponse<BookingsSummaryData>;
}

// ========== Admin Analytics ==========
export namespace AnalyticsAPI {
  export const AnalyticsCitySchema = z.object({
    id: z.string(),
    name: z.string(),
  });

  export const AnalyticsSummarySchema = z.object({
    totalSignups: z.number(),
    totalPayments: z.number(),
    totalRevenue: z.number(),
    activeSubscriptions: z.number(),
    newSubscriptions: z.number(),
    cancelledSubscriptions: z.number(),
    activeOneTime: z.number(),
    oneTimePayments: z.number(),
    oneTimeRevenue: z.number(),
    subscriptionPayments: z.number(),
    subscriptionRevenue: z.number(),
    totalActiveBookings: z.number(),
  });

  export const SignupsPerDaySchema = z.object({
    date: z.string(),
    count: z.number(),
  });

  export const SignupsPerCitySchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    count: z.number(),
  });

  export const PaymentsPerDaySchema = z.object({
    date: z.string(),
    count: z.number(),
    amount: z.number(),
  });

  // Stacked data has date + dynamic city keys with amounts
  export const StackedPerDaySchema = z.record(z.string(), z.union([z.string(), z.number()]));

  export const SubscriptionsPerDaySchema = z.object({
    date: z.string(),
    newSubs: z.number(),
  });

  export const BookingsPerDaySchema = z.object({
    date: z.string(),
    count: z.number(),
  });

  export const BookingsPerCitySchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    count: z.number(),
  });

  export const CityBreakdownSchema = z.object({
    cityId: z.string(),
    cityName: z.string(),
    revenue: z.number(),
    newSubscribers: z.number(),
    activeSubscribers: z.number(),
  });

  export const AnalyticsDataSchema = z.object({
    timeframe: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    cities: z.array(AnalyticsCitySchema),
    summary: AnalyticsSummarySchema,
    signups: z.object({
      perDay: z.array(SignupsPerDaySchema),
      perCity: z.array(SignupsPerCitySchema),
    }),
    payments: z.object({
      perDay: z.array(PaymentsPerDaySchema),
      perDayStacked: z.array(StackedPerDaySchema),
    }),
    subscriptions: z.object({
      perDay: z.array(SubscriptionsPerDaySchema),
      perDayStacked: z.array(StackedPerDaySchema),
    }),
    bookings: z.object({
      createdPerDay: z.array(BookingsPerDaySchema),
      byEventDay: z.array(BookingsPerDaySchema),
      perCity: z.array(BookingsPerCitySchema),
    }),
    cityBreakdown: z.array(CityBreakdownSchema),
  });

  export type AnalyticsCity = z.infer<typeof AnalyticsCitySchema>;
  export type CityBreakdown = z.infer<typeof CityBreakdownSchema>;
  export type AnalyticsSummary = z.infer<typeof AnalyticsSummarySchema>;
  export type SignupsPerDay = z.infer<typeof SignupsPerDaySchema>;
  export type SignupsPerCity = z.infer<typeof SignupsPerCitySchema>;
  export type PaymentsPerDay = z.infer<typeof PaymentsPerDaySchema>;
  export type SubscriptionsPerDay = z.infer<typeof SubscriptionsPerDaySchema>;
  export type BookingsPerDay = z.infer<typeof BookingsPerDaySchema>;
  export type BookingsPerCity = z.infer<typeof BookingsPerCitySchema>;
  export type AnalyticsData = z.infer<typeof AnalyticsDataSchema>;
  export type GetAnalyticsResponse = ApiResponse<AnalyticsData>;
}

// ========== Stats Endpoints ==========
export namespace StatsAPI {
  // GET /api/stats/quiz
  export const QuizStatsDataSchema = z.object({
    signupsByCity: z.record(z.string(), z.object({
      count: z.number(),
      cityName: z.string(),
    })),
    quizOptionPercentages: z.record(z.string(), z.record(z.string(), z.number())),
    totalUsers24h: z.number(),
  });

  export type QuizStatsData = z.infer<typeof QuizStatsDataSchema>;
  export type GetQuizStatsResponse = ApiResponse<QuizStatsData>;

  // POST /api/stats/quiz/interstitial
  export const InterstitialRequestSchema = z.object({
    questionId: z.string(),
    answer: z.union([z.string(), z.array(z.string()), z.number()]),
  });

  export const InterstitialResponseDataSchema = z.object({
    emoji: z.string(),
    message: z.string(),
  });

  export type InterstitialRequest = z.infer<typeof InterstitialRequestSchema>;
  export type InterstitialResponseData = z.infer<typeof InterstitialResponseDataSchema>;
  export type InterstitialResponse = ApiResponse<InterstitialResponseData>;
}

// ========== Landing Endpoints ==========

export namespace LandingAPI {
  // GET /api/landing/city -> the visitor's city, from edge geo headers.
  export const LandingCitySchema = z.object({ id: z.string(), name: z.string() });
  export type LandingCity = z.infer<typeof LandingCitySchema>;
  export type GetLandingCityResponse = ApiResponse<LandingCity | null>;

  // GET /api/landing/upcoming/events
  // date/time are the event's local wall-clock values ("YYYY-MM-DD", "HH:mm").
  export const UpcomingEventSchema = z.object({
    id: z.string(),
    date: z.string(),
    time: z.string(),
    type: z.string().nullable(),
    // Cities this date actually runs in, resolved from the event's
    // neighbourhoods. `null` means it runs everywhere. Additive: existing
    // consumers that ignore it are unaffected.
    cityIds: z.array(z.string()).nullable(),
  });

  export type UpcomingEvent = z.infer<typeof UpcomingEventSchema>;
  export type GetUpcomingEventsResponse = ApiResponse<UpcomingEvent[]>;

  // GET /api/landing/pricing
  //
  // Standard plan prices for the visitor's country, without an account. The
  // v3 onboarding stepper names the price during the questionnaire — ahead of
  // the auth gate — so it cannot use `/user/payment/payment-options`, which
  // requires a session (and applies that user's coupons and referrals).
  //
  // Deliberately narrower than `UserAPI.PaymentOption`: no Stripe price ids,
  // no coupon fields, nothing personalised. This is the rate card.
  export const PublicPriceSchema = z.object({
    id: z.string(),
    type: z.enum(["one_time", "subscription"]),
    name: z.string(),
    description: z.string(),
    /** Smallest currency unit (cents/pence) — render with `formatPrice`. */
    price: z.number(),
    interval_months: z.number().optional(),
  });

  export const GetPublicPricingDataSchema = z.object({
    currency: z.string(),
    /** No-show fee in this currency, smallest unit (booking disclaimer). */
    noShowFee: z.number(),
    options: z.array(PublicPriceSchema),
  });

  export type PublicPrice = z.infer<typeof PublicPriceSchema>;
  export type GetPublicPricingData = z.infer<typeof GetPublicPricingDataSchema>;
  export type GetPublicPricingResponse = ApiResponse<GetPublicPricingData>;
}

// ========== Chat Endpoints ==========
export namespace ChatAPI {
  // POST /api/chat/users - Get user info for chat display
  export const GetUsersRequestSchema = z.object({
    userIds: z.array(z.string()),
  });

  export const ChatUserSchema = z.object({
    id: z.string(),
    name: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().nullable().optional(),
    profileImageUrl: z.string().nullable().optional(),
    hasDevice: z.boolean().optional(),
    devicePlatforms: z.array(z.string()).optional(),
  });

  export const GetUsersDataSchema = z.object({
    users: z.record(z.string(), ChatUserSchema),
  });

  export type GetUsersRequest = z.infer<typeof GetUsersRequestSchema>;
  export type ChatUser = z.infer<typeof ChatUserSchema>;
  export type GetUsersData = z.infer<typeof GetUsersDataSchema>;
  export type GetUsersResponse = ApiResponse<GetUsersData>;

  // POST /api/chat/messages - Send a message
  export const SendMessageRequestSchema = z.object({
    conversationId: z.string(),
    content: z.string().min(1).max(5000),
  });

  export const SendMessageDataSchema = z.object({
    messageId: z.string(),
  });

  export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;
  export type SendMessageData = z.infer<typeof SendMessageDataSchema>;
  export type SendMessageResponse = ApiResponse<SendMessageData>;

  // POST /api/chat/devices - Register FCM device token
  export const RegisterDeviceRequestSchema = z.object({
    fcmToken: z.string().min(1),
    platform: z.enum(["web", "ios", "android"]).optional(),
    deviceInfo: z.string().optional(),
  });

  export type RegisterDeviceRequest = z.infer<typeof RegisterDeviceRequestSchema>;
  export type RegisterDeviceResponse = ApiResponse<{ deviceId: string }>;

  // DELETE /api/chat/devices - Unregister FCM device token
  export const UnregisterDeviceRequestSchema = z.object({
    fcmToken: z.string().min(1),
  });

  export type UnregisterDeviceRequest = z.infer<typeof UnregisterDeviceRequestSchema>;
  export type UnregisterDeviceResponse = ApiResponse<void>;
}

// ========== Club Endpoints ==========
export namespace ClubAPI {
  export const ClubAudienceSchema = z.enum(["sapphic", "gay", "everyone"]);
  // draft = hidden; unopened = teased (visible + joinable, nothing inside yet);
  // active = live; archived = hidden.
  export const ClubStatusSchema = z.enum(["draft", "unopened", "active", "archived"]);

  // Minimum access level for a capability. Joining a club is always free;
  // "subscriber" additionally requires an active Outclose subscription.
  export const ClubAccessLevelSchema = z.enum(["member", "subscriber"]);

  // Per-club gating. Add a capability by adding a key with a default — rows
  // stored before the key existed pick up the default when parsed.
  export const ClubPermissionsSchema = z.object({
    readForum: ClubAccessLevelSchema.default("member"),
    writeForum: ClubAccessLevelSchema.default("subscriber"),
    votePolls: ClubAccessLevelSchema.default("member"),
    attendEvent: ClubAccessLevelSchema.default("subscriber"),
  });

  export type ClubAudience = z.infer<typeof ClubAudienceSchema>;
  export type ClubStatus = z.infer<typeof ClubStatusSchema>;
  export type ClubAccessLevel = z.infer<typeof ClubAccessLevelSchema>;
  export type ClubPermissions = z.infer<typeof ClubPermissionsSchema>;
  export type ClubCapability = keyof ClubPermissions;

  export const ClubSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    tagline: z.string().nullable(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    audience: ClubAudienceSchema,
    status: ClubStatusSchema,
    eventThreshold: z.number().int(),
    permissions: ClubPermissionsSchema,
    sortOrder: z.number().int(),
  });

  export type Club = z.infer<typeof ClubSchema>;

  // Members in one city vs. the club's threshold for an in-person event.
  export const CityProgressSchema = z.object({
    cityId: z.string(),
    cityName: z.string().nullable(),
    members: z.number(),
    threshold: z.number(),
  });

  export type CityProgress = z.infer<typeof CityProgressSchema>;

  // What members see about their own city. Member counts and the event
  // threshold stay internal (admins see them in AdminClubDetail).
  export const ViewerCitySchema = z.object({
    cityId: z.string(),
    cityName: z.string().nullable(),
    meetupReady: z.boolean(), // enough members here — a meetup is being planned
  });

  export type ViewerCity = z.infer<typeof ViewerCitySchema>;

  // User-facing club: no internal threshold.
  export const ClubSummarySchema = ClubSchema.omit({ eventThreshold: true }).extend({
    memberCount: z.number(),
    isMember: z.boolean(),
    // The viewer's own city; null when the user has no city.
    city: ViewerCitySchema.nullable(),
  });

  export type ClubSummary = z.infer<typeof ClubSummarySchema>;

  export const ClubAccessSchema = z.object({
    isOpen: z.boolean(), // false for an unopened club: nothing is unlocked yet
    isMember: z.boolean(),
    isSubscriber: z.boolean(),
    can: z.object({
      readForum: z.boolean(),
      writeForum: z.boolean(),
      votePolls: z.boolean(),
      attendEvent: z.boolean(),
    }),
  });

  export type ClubAccess = z.infer<typeof ClubAccessSchema>;

  export const ClubPostAuthorSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    profileImageUrl: z.string().nullable(),
  });

  export const ClubPostSchema = z.object({
    id: z.string(),
    clubId: z.string(),
    parentId: z.string().nullable(),
    title: z.string().nullable(), // null on replies (and posts from before titles)
    body: z.string(),
    isAdminPost: z.boolean(),
    youtubeVideoId: z.string().nullable(), // admin posts only
    pinnedAt: z.string().nullable(),
    createdAt: z.string(),
    author: ClubPostAuthorSchema.nullable(),
    replyCount: z.number(),
    isOwn: z.boolean(),
  });

  export type ClubPost = z.infer<typeof ClubPostSchema>;

  export const ClubEventSchema = z.object({
    id: z.string(),
    slug: z.string().nullable(),
    name: z.string(),
    date: z.string(),
    time: z.string(),
    coverImageUrl: z.string().nullable(),
    cityIds: z.array(z.string()),
  });

  export type ClubEvent = z.infer<typeof ClubEventSchema>;

  // GET /api/user/clubs/:slug
  export const ClubDetailSchema = ClubSummarySchema.extend({
    access: ClubAccessSchema,
    pinnedPosts: z.array(ClubPostSchema),
    // Active polls; typed loosely to avoid a circular import of PollAPI.
    polls: z.array(z.any()),
    upcomingEvents: z.array(ClubEventSchema),
  });

  export type ClubDetail = Omit<z.infer<typeof ClubDetailSchema>, "polls"> & {
    polls: PollAPI.UserPoll[];
  };

  export const ClubPostThreadSchema = z.object({
    post: ClubPostSchema,
    replies: z.array(ClubPostSchema),
  });

  export type ClubPostThread = z.infer<typeof ClubPostThreadSchema>;

  export const ClubPostPageSchema = z.object({
    posts: z.array(ClubPostSchema),
    nextCursor: z.string().nullable(),
  });

  export type ClubPostPage = z.infer<typeof ClubPostPageSchema>;

  // POST /api/user/clubs/:slug/posts
  export const CreatePostRequestSchema = z.object({
    title: z.string().trim().max(150).nullable().optional(),
    body: z.string().trim().min(1).max(5000),
    parentId: z.string().nullable().optional(),
  }).refine((p) => !!p.parentId || !!p.title, { message: "A title is required", path: ["title"] });

  export type CreatePostRequest = z.infer<typeof CreatePostRequestSchema>;

  export const ReportPostRequestSchema = z.object({
    reason: z.enum(["spam", "inappropriate", "harassment", "other"]),
    description: z.string().max(1000).optional(),
  });

  export type ReportPostRequest = z.infer<typeof ReportPostRequestSchema>;

  // ----- Admin -----
  export const CreateClubRequestSchema = z.object({
    slug: z.string().trim().min(2).max(60).regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers and dashes only"),
    name: z.string().trim().min(1).max(100),
    tagline: z.string().max(200).nullable().optional(),
    description: z.string().max(10000).nullable().optional(),
    imageUrl: z.string().url().nullable().optional(),
    audience: ClubAudienceSchema,
    status: ClubStatusSchema.optional(),
    eventThreshold: z.number().int().min(1).max(1000).optional(),
    permissions: ClubPermissionsSchema.partial().optional(),
    sortOrder: z.number().int().optional(),
  });

  export type CreateClubRequest = z.infer<typeof CreateClubRequestSchema>;

  export const UpdateClubRequestSchema = CreateClubRequestSchema.partial();
  export type UpdateClubRequest = z.infer<typeof UpdateClubRequestSchema>;

  // Any YouTube link (or bare id), normalised to the video id; "" or null clears it.
  const YouTubeVideoSchema = z
    .string()
    .trim()
    .nullable()
    .transform((v, ctx) => {
      if (!v) return null;
      const id = parseYouTubeId(v);
      if (!id) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Not a YouTube video link" });
      return id;
    });

  export const AdminCreatePostRequestSchema = z.object({
    title: z.string().trim().min(1).max(150),
    body: z.string().trim().min(1).max(10000),
    pinned: z.boolean().optional(),
    youtubeUrl: YouTubeVideoSchema.optional(),
  });

  export type AdminCreatePostRequest = z.infer<typeof AdminCreatePostRequestSchema>;

  export const AdminUpdatePostRequestSchema = AdminCreatePostRequestSchema.partial();
  export type AdminUpdatePostRequest = z.infer<typeof AdminUpdatePostRequestSchema>;

  export const AdminClubSchema = ClubSchema.extend({
    memberCount: z.number(),
    readyCities: z.number(),
    pendingReports: z.number(),
  });

  export type AdminClub = z.infer<typeof AdminClubSchema>;

  export const AdminClubDetailSchema = z.object({
    club: ClubSchema,
    memberCount: z.number(),
    cities: z.array(CityProgressSchema.extend({
      ready: z.boolean(),
      hasUpcomingEvent: z.boolean(),
    })),
    posts: z.array(ClubPostSchema),
    polls: z.array(z.any()),
    reports: z.array(z.object({
      id: z.string(),
      postId: z.string(),
      postBody: z.string(),
      reason: z.string(),
      description: z.string().nullable(),
      createdAt: z.string(),
    })),
  });

  export type AdminClubDetail = Omit<z.infer<typeof AdminClubDetailSchema>, "polls"> & {
    polls: PollAPI.Poll[];
  };
}
